import React, { useEffect, useRef, useState } from 'react'
import {
  CAlert,
  CButton,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { state, uuidV4 } from '../lib/store'
import { aiCallImporterAgent, aiUsageRecord, aiSetCostContext } from '../lib/ai'
import { csLoadRuntimeTemplates, csBuildSingleFile, csPersistAndPublish, csProposeToKB, csCmdLabel, csFieldNoWrite } from '../lib/builder'
import { normalizeReplacementChars } from '../lib/parsers'
import { useUI } from '../../../shared/ui/UIProvider'
import { edApplyScript } from './EditorView'
import { triageSst, triageToPromptBlock, buildFichaMd, irToHumanText, getRequiredIRSummary } from '../lib/triage'
import { buildFormsDraft, formsDraftToPromptBlock } from '../lib/formsDraft'
import DiagnosticsModal from '../components/DiagnosticsModal'
import SstInterpreterPreview from '../components/SstInterpreterPreview'
import AgentTrace from '../components/AgentTrace'
import { checkCoverage } from '../lib/coverageCheck'
import { lintDataJson, lintErrors, lintFixPrompt } from '../lib/dataJsonLint'
import { normalizeDataJson } from '../lib/dataJsonNormalize'
import { validateRepairPreservation } from '../lib/dataJsonRepairGuard'
import { decompileSsc as decompileSscV2 } from '../lib/sscDecompiler'
import { convertSstToBoston } from '../lib/sstConverter/convertSst'
import { buildDeterministicSstReviewIR } from '../lib/sstConverter/reviewIr'
import { buildSstConversionOptions, expandSstImportFiles } from '../lib/sstImportBundle'
import { biaUploadSourceFile, devQueuePush } from '../lib/biaApi'

/* Port fiel de viewAiImport/bindAiImport/bindAiPreview de boston_ia:
   Importador IA de archivos .sst/.ssc en CADENA de 2 fases:
     Fase 1: Agente 1 (.sst) o Agente 2 (.ssc) interpreta el archivo → IR JSON
     Fase 2: Agente 3 convierte el IR → Custom Script data.json (chunked si hay >=2 subflows)
   + chat de EDICION del data.json generado, chat avanzado de refinamiento del IR,
   + Agente 4 (logic.json), envio a Revision Dev y publicacion.
   La capa IA (aiCallImporterAgent / aiUsageRecord) vive en lib/ai.js; aca va SOLO la orquestacion. */

function debugAgent1Enabled() {
  try {
    return new URLSearchParams(window.location.search).get('debugAgent1') === '1'
  } catch (_) {
    return false
  }
}

function debugAgent1IR(label, ir) {
  if (!debugAgent1Enabled()) return
  console.debug(label, {
    keys: Object.keys(ir || {}),
    metaSummary: ir?.meta?.summary || '',
    rootSummary: ir?.summary || '',
    wrapped: Boolean(ir?.data || ir?.ir || ir?.result),
  })
}

/* ===== Helpers (logica VERBATIM del original) ===== */

// Helper: detectar si un texto parece IR JSON (ir_version field)
export function aiIsIR(obj) {
  return obj && typeof obj === 'object' && obj.ir_version
}

export function validateAgent4LogicMerge(dataJson, logicJson) {
  if (!logicJson || typeof logicJson !== 'object' || Array.isArray(logicJson)) {
    throw new Error('logic.json debe ser un objeto JSON.')
  }
  const conflicts = ['onLoad', 'autoRun', 'actions'].filter(
    (key) => dataJson && dataJson[key] !== undefined && logicJson[key] !== undefined,
  )
  if (
    Array.isArray(dataJson?.postCommands) &&
    dataJson.postCommands.length > 0 &&
    logicJson.postCommands !== undefined
  ) {
    conflicts.push('postCommands')
  }
  if (conflicts.length) {
    throw new Error('logic.json duplica llaves ya presentes en data.json y el runtime las ignoraría: ' + conflicts.join(', '))
  }
  return logicJson
}

// Helper: detectar si un archivo es binario .ssc (por extension o contenido)
export function aiFileIsSSC(file) {
  if (file && file.decompiled && !file.isBinary) return false // .ssc decompilado a fuente → camino Agente 1
  if (!file) return false
  if (file.isBinary) return true
  const ext = (file.type || '').toLowerCase()
  return ext === 'ssc'
}

// El convertidor determinista sólo toma fuente .sst real. Un .ssc decompilado
// conserva su ruta actual porque su pseudo-fuente puede ser incompleta.
export function aiFileIsSourceSST(file) {
  if (!file || file.isBinary || file.decompiled || file.ssc) return false
  const ext = String(file.type || '').toLowerCase()
  return ext === 'sst' || /\.sst$/i.test(String(file.name || ''))
}

function isSstInterpreterResult(data) {
  return String(data?.executionMode || '').toLowerCase().trim() === 'sst_interpreter_v1'
}

// Extrae las cadenas ASCII legibles de un buffer binario (como `strings` de Linux).

/* ===== Card de TRIAGE deterministico (sin IA): riesgo, comandos→APIs, sensibles, huecos ===== */

/* Valida que el data.json sea EJECUTABLE: forms con campos O comandos directos.
   Un script SIN formulario es valido (ej. repricing automatico: onLoad + autoRun + ER). */
function dataJsonEjecutable(data) {
  if (!data) return false
  const tieneForms = Array.isArray(data.forms) && data.forms.some((f) => Array.isArray(f.remarks) && f.remarks.length)
  const tienePost = Array.isArray(data.postCommands) && data.postCommands.length > 0
  const tieneOnLoad = Array.isArray(data.onLoad) && data.onLoad.length > 0
  const tieneAutoRun = Boolean(data.autoRun)
  return tieneForms || tienePost || tieneOnLoad || tieneAutoRun
}

function cloneJSON(value) {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (_) {
    return value
  }
}

function isStrictSstDataJson(data, ir) {
  const mode =
    data?.executionMode ||
    data?.execution_mode ||
    data?.source_additional?.executionMode ||
    data?.source_additional?.execution_mode ||
    ir?.executionMode ||
    ir?.execution_mode ||
    ir?.source?.executionMode ||
    ir?.source?.execution_mode ||
    ''
  return String(mode).toLowerCase().trim() === 'sst_strict'
}

function irCommandPlan(ir) {
  if (Array.isArray(ir?.command_plan)) return ir.command_plan
  if (Array.isArray(ir?.commandPlan)) return ir.commandPlan
  if (Array.isArray(ir?.source_additional?.command_plan)) return ir.source_additional.command_plan
  if (Array.isArray(ir?.source_additional?.commandPlan)) return ir.source_additional.commandPlan
  return []
}

function preserveStrictSstCommandPlan(data, ir) {
  if (!data || typeof data !== 'object' || !isStrictSstDataJson(data, ir)) return data
  const plan = irCommandPlan(ir)
  if (!plan.length) return data
  const next = cloneJSON(data)
  const sourceAdditional =
    next.source_additional && typeof next.source_additional === 'object' && !Array.isArray(next.source_additional)
      ? { ...next.source_additional }
      : {}
  if (!Array.isArray(sourceAdditional.command_plan) || !sourceAdditional.command_plan.length) {
    sourceAdditional.command_plan = cloneJSON(plan)
  }
  next.source_additional = sourceAdditional
  return next
}

/* Card "Formularios detectados" — layout real extraído sin IA (convertidor humano) */
function FormsDraftCard({ d }) {
  return (
    <details
      className="bia-cat"
      style={{ margin: '2px 0 6px 12px', padding: '6px 10px', background: 'var(--brand-soft)', borderRadius: 8, border: '1px solid var(--border)' }}
    >
      <summary>
        🧩 Formularios detectados (sin IA) — <b>{d.stats.forms}</b> form(s) - <b>{d.stats.campos}</b> elementos
        {d.stats.botones ? (
          <span className="bia-badge ok" style={{ marginLeft: 6 }}>
            {d.stats.botones} botón(es) → actions
          </span>
        ) : null}
        {d.stats.menus ? (
          <span className="bia-badge warn" style={{ marginLeft: 6 }}>
            {d.stats.menus} menú(s)
          </span>
        ) : null}
      </summary>
      <div style={{ fontSize: 12, marginTop: 6 }}>
        {d.forms.map((f, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div>
              <strong>«{f.title || '(sin título)'}»</strong> - tipo: <code>{f.windowType}</code> - {f.totalFields} elementos
            </div>
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              {f.fields.map((fl, j) => (
                <li key={j}>
                  {fl.control === 'button' ? (
                    <>
                      🔘 <b>{fl.label}</b> <span style={{ color: 'var(--muted)' }}>→ candidato a action</span>
                    </>
                  ) : fl.control === 'checkbox_group' ? (
                    <>☑ {(fl.miembros || []).join(' - ')}</>
                  ) : (
                    <>
                      <code>[{fl.variable}]</code> «{fl.label}» - {fl.control}
                      {fl.mandatory ? <b>*</b> : null}
                      {fl.opciones ? <span style={{ color: 'var(--muted)' }}> - {fl.opciones.length} opciones</span> : null}
                      {fl.rol ? <span style={{ color: fl.rol === 'user_input' ? 'var(--brand-dark)' : '#b07b1a' }}> - {fl.rol}</span> : null}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {d.roles && d.roles.unused ? (
          <div style={{ color: 'var(--muted)' }}>{d.roles.unused} variables sin uso real (no se generan campos)</div>
        ) : null}
      </div>
    </details>
  )
}

function TriageCard({ t }) {
  const tone = t.nivel.startsWith('R1') || t.nivel.startsWith('R2') ? 'ok' : t.nivel.startsWith('R3') ? 'warn' : 'err'
  return (
    <details
      className="bia-cat"
      style={{ margin: '2px 0 6px 12px', padding: '6px 10px', background: 'var(--brand-soft)', borderRadius: 8, border: '1px solid var(--border)' }}
    >
      <summary>
        🔎 Análisis previo (sin IA) — <span className={`bia-badge ${tone}`}>{t.nivel}</span>
        {t.sensibles.length ? (
          <span className="bia-badge err" style={{ marginLeft: 6 }}>
            ⚠ {t.sensibles.length} sensibles
          </span>
        ) : null}
        {t.incompleto ? (
          <span className="bia-badge warn" style={{ marginLeft: 6 }}>
            fuente incompleto
          </span>
        ) : null}
      </summary>
      <div style={{ fontSize: 12, marginTop: 6 }}>
        <p style={{ margin: '4px 0' }}>{t.resumen}</p>
        <div>
          <strong>Comandos → clase → API destino:</strong>
        </div>
        <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
          {t.comandos.map((c, i) => (
            <li key={i}>
              <code>{c.cmd}</code> → {c.clase} → <span style={{ color: c.gap ? '#b03030' : 'var(--brand-dark)' }}>{c.api}</span>
            </li>
          ))}
        </ul>
        {t.sensibles.length ? (
          <>
            <div>
              <strong>Campos sensibles (PCI/PII):</strong>
            </div>
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              {t.sensibles.map((s, i) => (
                <li key={i}>
                  <code>[{s.campo}]</code> <b>{s.tipo}</b>: {s.detalle}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {t.huecos.definesSinUso.length ? (
          <p style={{ margin: '4px 0' }}>
            <strong>Defines sin uso ({t.huecos.definesSinUso.length}):</strong> {t.huecos.definesSinUso.slice(0, 12).join(', ')}
            {t.huecos.definesSinUso.length > 12 ? '…' : ''}
          </p>
        ) : null}
        {t.huecos.dependencias.length ? (
          <p style={{ margin: '4px 0' }}>
            <strong>Dependencias externas:</strong> {t.huecos.dependencias.join(', ')}
          </p>
        ) : null}
        {t.gaps.length ? (
          <p style={{ margin: '4px 0' }}>
            <strong>Sin endpoint dedicado (hoy: executeCommand + allowlist):</strong> {t.gaps.join(' - ')}
          </p>
        ) : null}
      </div>
    </details>
  )
}

function extractStringsFromBinary(uint8Array, minLength = 4) {
  const strings = []
  let current = ''
  for (let i = 0; i < uint8Array.length; i++) {
    const c = uint8Array[i]
    // ASCII imprimibles (32-126) + tab/newline
    if ((c >= 32 && c <= 126) || c === 9 || c === 10) {
      current += String.fromCharCode(c)
    } else {
      if (current.length >= minLength) strings.push(current)
      current = ''
    }
  }
  if (current.length >= minLength) strings.push(current)
  return strings
}

// DECOMPILADOR ESTRUCTURAL .ssc — formato reverseado (jun-2026, par C65I)
//   header 7 bytes; tabla de variables = [reclen u16][flags u16][maxlen u8][NOMBRE][00][blob]
//   flags: byte bajo = tipo (0x80=ANY, 0x01=NUM, 0x02=DECIMAL) - bit 0x100 = MAND
// Devuelve un volcado ESTRUCTURADO (texto) para el Agente 2, o null si la tabla no parsea.
function decompileSsc(u8) {
  try {
    const TYPES = { 0x80: 'ANY', 0x01: 'NUM', 0x02: 'DECIMAL', 0x04: 'ALPHA', 0x08: 'DATE', 0x10: 'TIME' }
    const vars = []
    let pos = 7
    while (pos + 6 < u8.length && vars.length < 400) {
      const reclen = u8[pos] | (u8[pos + 1] << 8)
      const flags = u8[pos + 2] | (u8[pos + 3] << 8)
      const maxlen = u8[pos + 4]
      if (reclen < 4 || reclen > 220) break
      let i = pos + 5
      let name = ''
      let okName = true
      while (i < u8.length && u8[i] !== 0 && name.length < 64) {
        const b = u8[i]
        if (!((b >= 32 && b <= 126) || b >= 0xa0)) {
          okName = false
          break
        }
        name += String.fromCharCode(b)
        i++
      }
      if (!okName || !name) break
      const tb = flags & 0xff
      vars.push({
        name: name,
        type: TYPES[tb] || 'tipo_0x' + tb.toString(16),
        maxlen: maxlen,
        mandatory: !!(flags & 0x100),
        hasDefault: (pos + 2 + reclen - (i + 1)) > 1,
      })
      pos = pos + 2 + reclen
    }
    if (vars.length < 5) return null
    const tail = extractStringsFromBinary(u8.slice(pos), 3)
    const vset = {}
    vars.forEach((v) => {
      vset[v.name.toUpperCase()] = 1
    })
    const opts = []
    const titles = []
    const cmds = []
    const labels = []
    const sys = {}
    const cmdRx = /^(\*|0|1|WP|W-|PQ|ER\b|ET\b|IR\b|EM\b|QP\/|5|3|6|7|9|AC|HOT|CF|EMUFIND:|HK\d)/
    tail.forEach((raw) => {
      const t = String(raw).trim()
      if (!t || vset[t.toUpperCase()]) return
      if (t.charAt(0) === '@') {
        sys[t] = 1
        return
      }
      if (t.indexOf('=') > 0 && /^.{1,40}=[^=|]{0,20}\|?$/.test(t)) {
        opts.push(t.replace(/\|$/, '').trim())
        return
      }
      if (cmdRx.test(t) && t.length <= 70) {
        cmds.push(t)
        return
      }
      if (t === t.toUpperCase() && t.length >= 4 && t.length <= 60 && t.indexOf(' ') >= 0) {
        titles.push(t)
        return
      }
      if (t.length >= 3 && t.length <= 40) labels.push(t)
    })
    const L = []
    L.push('=== DECOMPILADO ESTRUCTURADO .ssc (parser binario: tabla de variables real) ===')
    L.push('NOTA: tipo/longitud/obligatoriedad RECUPERADOS del binario (MAND 100%, tipo 99% validados).')
    L.push('NOTA: la tabla esta ordenada ALFABETICAMENTE, NO es el orden de pantalla.')
    L.push('')
    L.push('VARIABLES (' + vars.length + ') — nombre | tipo | maxlen | obligatorio | default:')
    vars.forEach((v) =>
      L.push('  ' + v.name + ' | ' + v.type + ' | ' + v.maxlen + ' | ' + (v.mandatory ? 'MAND' : 'OPT') + (v.hasDefault ? ' | def' : '')),
    )
    L.push('')
    L.push('OPCIONES DROPDOWN (' + opts.length + '):')
    opts.slice(0, 120).forEach((o) => L.push('  ' + o))
    L.push('')
    L.push('TITULOS DE VENTANA / TEXTOS LARGOS:')
    titles.slice(0, 40).forEach((t) => L.push('  ' + t))
    L.push('')
    L.push('COMANDOS HOST DETECTADOS:')
    cmds.slice(0, 80).forEach((c) => L.push('  ' + c))
    L.push('')
    L.push('TOKENS @SISTEMA: ' + Object.keys(sys).sort().join(' '))
    L.push('')
    L.push('LABELS / TEXTOS CORTOS:')
    labels.slice(0, 80).forEach((x) => L.push('  ' + x))
    return L.join('\n')
  } catch (e) {
    console.warn('[decompileSsc]', e)
    return null
  }
}

// Detecta si el archivo es binario (.ssc compilado) o texto (.sst legible).
// Los .ssc empiezan con bytes binarios tipo 03 00 00 D0...
function looksLikeBinaryScribe(uint8Array) {
  if (uint8Array.length < 8) return false
  // Primeros bytes son no-ASCII (< 32 y no son tab/newline)
  let nonAscii = 0
  for (let i = 0; i < Math.min(32, uint8Array.length); i++) {
    const c = uint8Array[i]
    if (c !== 0 && c < 32 && c !== 9 && c !== 10 && c !== 13) nonAscii++
  }
  return nonAscii >= 4
}

/* ===== LECCIONES APRENDIDAS — captura "como se resolvio" (NO el script completo).
   Quedan PENDIENTES hasta el OK humano; aprobadas se inyectan a los agentes. ===== */
const lecQueue = {
  KEY: 'boston_kb_lecciones_v1',
  all() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY) || '[]')
    } catch (e) {
      return []
    }
  },
  saveAll(l) {
    localStorage.setItem(this.KEY, JSON.stringify(l))
  },
  add(lec) {
    const l = this.all()
    lec.id = 'lec_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)
    lec.ts = new Date().toISOString()
    lec.status = 'pendiente'
    l.push(lec)
    this.saveAll(l)
    return lec
  },
}

// Resume un diff de devDiffData en UNA linea ("lo que aplico", no el script)
function lecResumirDiff(diff) {
  if (!diff) return ''
  const p = []
  if (diff.added && diff.added.length) p.push('+campos: ' + diff.added.slice(0, 6).join(','))
  if (diff.removed && diff.removed.length) p.push('-campos: ' + diff.removed.slice(0, 6).join(','))
  if (diff.changed && diff.changed.length)
    p.push('~' + diff.changed.slice(0, 6).map((c) => c.tag + '(' + c.props.slice(0, 4).join(',') + ')').join(' - '))
  if (diff.topChanged && diff.topChanged.length) p.push('top-level: ' + diff.topChanged.join(','))
  return p.join(' | ').slice(0, 400)
}

// Diff campo-a-campo del data.json (original vs editado)
function devDiffData(origStr, curStr) {
  let A, B
  try {
    A = JSON.parse(origStr || '{}')
    B = JSON.parse(curStr || '{}')
  } catch (e) {
    return null
  }
  const fieldsOf = (d) => {
    const m = {}
    ;(d.forms || []).forEach((f) =>
      (f.remarks || []).forEach((r, i) => {
        m[r.tag || r.campoForm || 'idx_' + i] = r
      }),
    )
    return m
  }
  const fa = fieldsOf(A)
  const fb = fieldsOf(B)
  const added = Object.keys(fb).filter((k) => !(k in fa))
  const removed = Object.keys(fa).filter((k) => !(k in fb))
  const changed = []
  Object.keys(fb)
    .filter((k) => k in fa)
    .forEach((k) => {
      const props = []
      const all = new Set(Object.keys(fa[k] || {}).concat(Object.keys(fb[k] || {})))
      all.forEach((p) => {
        if (JSON.stringify(fa[k][p]) !== JSON.stringify(fb[k][p])) props.push(p)
      })
      if (props.length) changed.push({ tag: k, props })
    })
  const topChanged = []
  new Set(Object.keys(A).concat(Object.keys(B))).forEach((k) => {
    if (k === 'forms') return
    if (JSON.stringify(A[k]) !== JSON.stringify(B[k])) topChanged.push(k)
  })
  return { added, removed, changed, topChanged }
}

/* ===== Generacion CHUNKED (scripts grandes: 1 llamada por sub-flujo, se mergean) ===== */
async function aiGenerateChunked(ir) {
  const imp = state.ai.imp
  const subs = (ir && ir.subflows) || []
  const warnings = []
  imp.chunk = { done: 0, total: subs.length + 1, label: 'menu / base' }
  emit()
  const baseR = await aiCallImporterAgent(
    'agent3_gen',
    'MODO CHUNK\nPARTE: BASE\nDevolve el data.json con top-level + form1 (un button_menu por cada subflow del IR) + form2 con remarks vacio [].\n\nIR:\n' +
      JSON.stringify(ir, null, 2),
    true,
    { generationMode: 'chunk', referenceIr: ir },
  )
  if (baseR.usage) aiUsageRecord(baseR.model, baseR.usage, 'chunk_base')
  if (!baseR.ok || !baseR.data || !baseR.data.data) throw new Error('parte BASE: ' + ((baseR.data && baseR.data.error) || 'sin data.json'))
  const dj = baseR.data.data
  if (baseR.data.warnings && baseR.data.warnings.length) warnings.push.apply(warnings, baseR.data.warnings)
  dj.forms = dj.forms || []
  let form2 = dj.forms.find(function (f) {
    return f.form === 'form2'
  })
  if (!form2) {
    form2 = { form: 'form2', texto: '', title: '', title_visible: false, remarks: [] }
    dj.forms.push(form2)
  }
  if (!Array.isArray(form2.remarks)) form2.remarks = []
  let n = 0
  for (const sf of subs) {
    n++
    imp.chunk = { done: n, total: subs.length + 1, label: sf.title || sf.slug || 'sub-flujo ' + n }
    emit()
    const r = await aiCallImporterAgent(
      'agent3_gen',
      'MODO CHUNK\nPARTE: SUBFLUJO\nSUBFLUJO: ' +
        sf.slug +
        '\nGenera SOLO {"remarks":[...]} con los fields de ESTE sub-flujo (grupo *' +
        sf.slug +
        '* + su composite block:99 si arma comando). Nada del resto.\n\nIR:\n' +
        JSON.stringify(ir, null, 2),
      true,
      { generationMode: 'chunk', referenceIr: ir },
    )
    if (r.usage) aiUsageRecord(r.model, r.usage, 'chunk_sub')
    const part = r.data && r.data.data
    let fields = Array.isArray(part) ? part : (part && (part.remarks || (part.forms && part.forms[0] && part.forms[0].remarks))) || []
    if (!r.ok || !fields || !fields.length) {
      warnings.push('Sub-flujo "' + sf.slug + '" no devolvio campos - revisalo a mano.')
      continue
    }
    form2.remarks.push.apply(form2.remarks, fields)
    if (r.data.warnings && r.data.warnings.length) warnings.push.apply(warnings, r.data.warnings)
  }
  imp.chunk = null
  return { data: dj, warnings: warnings }
}

// Decide chunked (IR con >=2 subflows) o llamada unica. Devuelve la MISMA forma que aiCallImporterAgent.
async function aiGenerateScript(ir, prompt) {
  const subs = (ir && ir.subflows) || []
  if (subs.length >= 2) {
    try {
      const res = await aiGenerateChunked(ir)
      return { ok: true, data: { data: res.data, warnings: res.warnings }, model: state.ai.model, usage: null, raw: '' }
    } catch (e) {
      state.ai.imp.chunk = null
      return {
        ok: false,
        data: {
          error: 'Generacion chunked fallo (' + e.message + ')',
          hint: 'Reintenta; si persiste revisa el IR o genera el sub-flujo problematico a mano.',
        },
        model: state.ai.model,
        usage: null,
        raw: '',
      }
    }
  }
  return await aiCallImporterAgent('agent3_gen', prompt, true, { generationMode: 'import', referenceIr: ir })
}

/* ===== Sub-render: linea "escribe:" de un campo (IIFE del original) ===== */
function fieldWriteLine(f) {
  const code = f.block != null && f.block !== '' && !isNaN(f.block) && Number(f.block) < 90 ? String(f.block) : ''
  const val = f.dato != null && f.dato !== '' ? f.dato : '«' + (f.label || f.tag || 'valor') + '»'
  const full = code + (f.remark_mask || '').replace(/\[[^\]]*\]/g, val)
  return full
}

/* ===== Port de renderScriptFieldsPreview: vista visual de campos por paso ===== */
function ScriptFieldsPreview({ r }) {
  const forms = (r && r.forms) || []
  const post = (r && r.postCommands) || []
  return (
    <>
      <h4 style={{ margin: '14px 0 8px', fontSize: 14 }}>📋 Campos del formulario (vista del agente)</h4>
      <div style={{ fontSize: 11, color: 'var(--muted)', margin: '-4px 0 8px' }}>
        Las marcadas{' '}
        <span className="bia-pill mini" style={{ background: 'rgba(148,163,184,.25)', color: 'inherit' }}>
          🔒 auto - oculta
        </span>{' '}
        las arma el script solo (grupo oculto): el vendedor no las ve, pero igual se escriben.
      </div>
      {forms.length === 0 ? (
        <p className="bia-empty">Sin forms — solo postCommands.</p>
      ) : (
        forms.map((form, fi) => (
          <div key={fi} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {forms.length > 1 ? (
                <span className="bia-pill mini">
                  Paso {fi + 1} de {forms.length}
                </span>
              ) : null}
              <strong style={{ fontSize: 14 }}>{form.title || form.form || 'Form ' + (fi + 1)}</strong>
            </div>
            {(form.remarks || []).length === 0 ? (
              <p className="bia-empty" style={{ padding: 8, fontSize: 12 }}>
                Sin campos en este paso.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.remarks.map((field, idx) => {
                  const hidden = /oculto/i.test(field.group || '')
                  const writeLine = fieldWriteLine(field)
                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: hidden ? 'rgba(148,163,184,.12)' : 'var(--bg)',
                        borderRadius: 4,
                        fontSize: 12.5,
                        opacity: hidden ? 0.6 : 1,
                      }}
                    >
                      <code
                        className="bia-mono"
                        style={{ background: '#0f172a', color: '#22d989', padding: '2px 6px', borderRadius: 3, fontSize: 11, flexShrink: 0 }}
                      >
                        {field.campoForm || 'field_' + idx}
                      </code>
                      <div style={{ flex: 1 }}>
                        <strong>{field.label || '(sin label)'}</strong>
                        {field.mandatory ? <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span> : null}
                        {hidden ? (
                          <span
                            className="bia-pill mini"
                            style={{ marginLeft: 6, background: 'rgba(148,163,184,.25)', color: 'inherit' }}
                            title="Línea automática: el script la arma sola y va en un grupo oculto; el vendedor no la ve"
                          >
                            🔒 auto - oculta
                          </span>
                        ) : null}
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          <span className="bia-pill mini">{field.typedata || 'text'}</span>{' '}
                          {field.typeremark && field.typeremark !== 'General' ? (
                            <span className="bia-pill mini">{field.typeremark}</span>
                          ) : null}{' '}
                          {csFieldNoWrite(field) ? (
                            <span
                              className="bia-pill mini"
                              title="Dato interno o elemento visual: el runtime NO emite ningún comando por este campo"
                              style={{ opacity: 0.75 }}
                            >
                              no escribe
                            </span>
                          ) : writeLine ? (
                            <>
                              - escribe: <code>{writeLine}</code>
                            </>
                          ) : null}{' '}
                          {field.data_test ? (
                            <>
                              - Ej: <code>{String(field.data_test)}</code>
                            </>
                          ) : null}{' '}
                          {(field.datasource || []).length ? '- ' + field.datasource.length + ' opciones' : ''}
                        </div>
                        {(field.datasource || []).length ? (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                            {field.datasource.slice(0, 5).map((o, oi) => (
                              <code key={oi} style={{ background: 'rgba(0,0,0,.05)', padding: '1px 4px', borderRadius: 2, marginRight: 3 }}>
                                {o.text || o.value || ''}
                              </code>
                            ))}
                            {field.datasource.length > 5 ? <small> ...y {field.datasource.length - 5} más</small> : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))
      )}
      {post.length ? (
        <>
          <h4 style={{ margin: '14px 0 8px', fontSize: 14 }}>⚡ postCommands ({post.length})</h4>
          <ol
            className="bia-mono"
            style={{ margin: 0, padding: '10px 14px 10px 32px', background: '#0f172a', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }}
          >
            {post.map((c, ci) => (
              <li key={ci}>
                <code style={{ color: '#22d989' }}>{csCmdLabel(c)}</code>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </>
  )
}

/* ===== Sub-render: log de chat (clases bia-chat del tema) ===== */
function ChatLog({ messages }) {
  if (!messages || !messages.length) return null
  return (
    <div className="bia-chat" style={{ marginBottom: 8 }}>
      {messages.map((m, i) => (
        <div key={i} className={`bia-chat-msg ${m.role === 'user' ? 'user' : 'assistant'}`}>
          <strong style={{ marginRight: 6 }}>{m.role === 'user' ? '👤 Vos:' : '🤖 IA:'}</strong>
          {m.content}
        </div>
      ))}
    </div>
  )
}

function AiImportView() {
  const state = useBostonState()
  const { toast } = useUI()
  const imp = state.ai.imp
  const fileRef = useRef(null)
  const [instructions, setInstructions] = useState(imp.instructions || '')
  const [chatInput, setChatInput] = useState('')
  const [editInput, setEditInput] = useState('')
  const [copiedHandler, setCopiedHandler] = useState(false)
  // Preview funcional (port de showPreviewModal): single-file en iframe
  const [pv, setPv] = useState(null)
  const pvRef = useRef(null)
  // Envio a Revision Dev (reemplaza el prompt() nativo)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewMotivo, setReviewMotivo] = useState('')
  const [reviewDraft, setReviewDraft] = useState(null)
  const [reviewSending, setReviewSending] = useState(false)
  const [diagOpen, setDiagOpen] = useState(false)
  const [sstPreviewBusy, setSstPreviewBusy] = useState('')
  const [sstPrototypeReport, setSstPrototypeReport] = useState(null)
  const mockModeEnabled = new URLSearchParams(window.location.search).get('mock') === '1'

  const hasKey = true
  const busy = imp.busyInterpret || imp.busy

  /* ===== Carga de archivos (logica VERBATIM: decompilado .ssc + normalizacion 1252) ===== */
  async function onFiles(e) {
    let list
    try {
      list = await expandSstImportFiles(Array.from(e.target.files || []))
    } catch (error) {
      toast('No se pudo abrir el ZIP: ' + (error.message || String(error)), 'danger')
      e.target.value = ''
      return
    }
    if (!list.length) {
      toast('El ZIP no contiene archivos SST, SSC, CSV, XLS, XLSX o TXT compatibles.', 'warning')
      e.target.value = ''
      return
    }
    const importId = imp.importId || uuidV4()
    imp.importId = importId
    for (const picked of list) {
      const f = picked.file
      const displayName = picked.relativePath || f.name
      let sourceFile = null
      const ext = displayName.split('.').pop().toLowerCase()
      const fileRole = /^(csv|xls|xlsx)$/.test(ext) ? 'catalog' : 'script_source'
      if (/\.(sst|ssc|csv|xls|xlsx)$/i.test(displayName)) {
        try {
          sourceFile = await biaUploadSourceFile(f, { importId, uploadId: uuidV4(), fileRole, targetEnv: state.cs?.targetEnv || 'stg' }) // eslint-disable-line no-await-in-loop
        } catch (error) {
          toast('No se pudo conservar el archivo original «' + displayName + '»: ' + (error.message || String(error)), 'danger')
          continue
        }
      }
      const buf = new Uint8Array(await f.arrayBuffer())
      let content
      let isBinary = false
      let decompiled = false
      if (ext === 'xls' || ext === 'xlsx') {
        content = `[CATÁLOGO EXCEL BINARIO: ${displayName} · ${buf.length} bytes · original conservado para descarga]`
        isBinary = true
      } else if (ext === 'ssc' || looksLikeBinaryScribe(buf)) {
        // .ssc compilado: DECOMPILADOR v2 (bytecode completo → pseudo-.sst fiel:
        // defines, dropdowns, comandos EN ORDEN, ventanas, IF/GOTO). Si decompila,
        // el archivo se trata como FUENTE → camino Agente 1 + triage completo.
        const v2 = decompileSscV2(buf)
        if (v2 && v2.ok && v2.sst) {
          content = v2.sst
          decompiled = true
          // isBinary queda false → triage + Agente 1
        } else {
          // Fallback: decompilador estructural viejo (solo tabla) + strings sueltas
          const structured = decompileSsc(buf)
          const strings = extractStringsFromBinary(buf, 3)
          if (structured) {
            content = structured + '\n\n=== STRINGS CRUDOS (apendice) ===\n' + strings.join('\n')
            decompiled = true
          } else {
            content = `[ARCHIVO .SSC COMPILADO - extraídas ${strings.length} strings legibles del bytecode]\n\n` + strings.join('\n')
          }
          isBinary = true
        }
      } else {
        // .sst / .csv / .txt: texto plano
        // Detectar y normalizar Windows-1252 (chevrones corruptos comunes en Sabre)
        let text = new TextDecoder('utf-8', { fatal: false }).decode(buf)
        if (text.includes('ï¿½')) {
          try {
            text = new TextDecoder('windows-1252').decode(buf)
          } catch {}
        }
        content = text
      }
      const entry = {
        name: displayName,
        relativePath: displayName,
        archiveName: picked.archiveName || '',
        fromArchive: picked.fromArchive === true,
        type: ext,
        size: buf.length,
        content,
        isBinary,
        decompiled,
        sourceFile,
        fileRole,
      }
      // TRIAGE deterministico local (sin IA): riesgo, comandos→API, sensibles, huecos
      if (!isBinary) {
        try {
          aiSetCostContext({ scriptName: displayName }) // costo por script: nueva corrida
          entry.triage = triageSst(content, displayName)
        } catch (err) {
          console.warn('[triage]', err)
        }
        // FORMULARIOS DETECTADOS (convertidor humano vendorizado): layout real sin IA
        try {
          const fd = buildFormsDraft(content, displayName)
          if (fd && fd.ok) entry.formsDraft = fd
        } catch (err) {
          console.warn('[formsDraft]', err)
        }
      }
      imp.files.push(entry)
    }
    e.target.value = '' // el render() original recreaba el input
    emit()
  }

  function removeFile(idx) {
    const removed = imp.files[idx]
    imp.files.splice(idx, 1)
    if (!imp.files.length) imp.importId = ''
    // reset el flujo si quitamos archivos
    imp.interpretation = ''
    imp.interpretationApproved = false
    imp.lastResult = null
    imp.logicJson = null
    imp.converterIr = null
    imp.conversionMode = ''
    if (removed && sstPrototypeReport?.fileName === removed.name) setSstPrototypeReport(null)
    emit()
  }

  async function previewDeterministicSst(file) {
    if (!mockModeEnabled) return
    if (!file || sstPreviewBusy) return
    setSstPreviewBusy(file.name)
    try {
      const conversion = convertSstToBoston(normalizeReplacementChars(String(file.content || '')), {
        ...buildSstConversionOptions(file, imp.files),
      })
      const adapted = conversion.boston
      if (!adapted?.dataJson || !Array.isArray(adapted.dataJson.forms) || !adapted.dataJson.forms.length) {
        throw new Error('El convertidor no produjo formularios compatibles con la vista Boston.')
      }
      const report = {
        fileName: file.name,
        ...adapted.stats,
        warnings: (adapted.diagnostics || []).filter((item) => String(item.level || '').toLowerCase() !== 'info').length,
        logicBlocks: Object.keys(adapted.logicJson?.blocks || {}).length,
      }
      setSstPrototypeReport(report)
      await openPreview(
        adapted.dataJson,
        { config: { uuid: 'sst-preview-' + Date.now(), useInternalConfig: true } },
        {
          prototype: true,
          prototypeStats: report,
          logicJson: adapted.logicJson,
          ir: conversion.ir,
        },
      )
    } catch (error) {
      console.error('[sst converter preview]', error)
      toast('No se pudo construir la vista previa SST: ' + (error.message || String(error)), 'danger')
    } finally {
      setSstPreviewBusy('')
    }
  }

  /* ===== FASE 1 + FASE 2 en cadena (port de aiInterpretFiles) ===== */
  async function aiInterpretFiles() {
    if (imp.busyInterpret || imp.busy) return
    if (!imp.files.length) {
      toast('Cargá al menos un archivo.', 'warning')
      return
    }

    for (const f of imp.files) {
      if (f.content && f.content.includes('ï¿½')) {
        const fixed = normalizeReplacementChars(f.content)
        if (fixed !== f.content) f.content = fixed
      }
    }

    // Preferencia .sst > .ssc: el SST conserva la lógica completa y se interpreta
    // localmente; el .ssc mantiene su ruta histórica por Agente 2.
    const sstFiles = imp.files.filter((f) => !aiFileIsSSC(f))
    const sourceSstFiles = sstFiles.filter(aiFileIsSourceSST)
    const sscFiles = imp.files.filter((f) => aiFileIsSSC(f))
    let firstFile, useSSC, filesToSend
    if (sourceSstFiles.length) {
      firstFile = sourceSstFiles[0]
      useSSC = false
      filesToSend = sstFiles
      imp.sscOnly = false
      imp.sscIgnored = sscFiles.map((f) => f.name)
    } else if (sstFiles.length) {
      firstFile = sstFiles[0]
      useSSC = false
      filesToSend = sstFiles
      imp.sscOnly = false
      imp.sscIgnored = sscFiles.map((f) => f.name)
    } else {
      firstFile = imp.files[0]
      useSSC = aiFileIsSSC(firstFile)
      filesToSend = imp.files
      imp.sscOnly = useSSC
      imp.sscIgnored = []
    }
    const agent = useSSC ? 'agent2_ssc' : 'agent1_sst'
    imp.usedAgent = agent

    if (!useSSC && firstFile && aiFileIsSourceSST(firstFile)) {
      imp.busyInterpret = true
      imp.phase = 'interpret_sst_local'
      imp.lastResult = null
      imp.logicJson = null
      imp.converterIr = null
      imp.conversionMode = ''
      imp.lastRawError = null
      imp.usedAgent = 'sst_converter_v1'
      emit()
      try {
        console.log('[sst-import] skip agent1 reason=deterministic-sst', firstFile.name)
        const conversion = convertSstToBoston(normalizeReplacementChars(String(firstFile.content || '')), {
          ...buildSstConversionOptions(firstFile, imp.files),
        })
        const adapted = conversion.boston
        if (!adapted?.dataJson || !adapted?.logicJson?.entryBlock || !Object.keys(adapted.logicJson.blocks || {}).length) {
          throw new Error('El convertidor SST no produjo un IR local con data.json + logic.json ejecutables.')
        }
        const ir = buildDeterministicSstReviewIR(conversion, { fileName: firstFile.name })
        getRequiredIRSummary(ir)
        imp.ir = ir
        imp.converterIr = conversion.ir
        imp.interpretation = irToHumanText(ir)
        imp.interpretationApproved = false
      } catch (err) {
        imp.ir = null
        imp.interpretation = ''
        imp.interpretationApproved = false
        imp.lastRawError = {
          message: err.message || String(err),
          hint: 'Revisá los diagnósticos del convertidor SST local.',
          raw: '',
          rawExcerpt: '',
        }
      } finally {
        imp.busyInterpret = false
        imp.phase = null
        emit()
      }
      return
    }

    const filesBlock = filesToSend
      .map(
        (f) =>
          `=== ARCHIVO: ${f.name} (tipo: ${f.type}, ${f.size} chars${f.isBinary ? ', binario .ssc - strings extraídos' : f.decompiled ? ', pseudo-fuente decompilado de .ssc (descripciones de defines y nombres de labels NO disponibles)' : ''}) ===\n${f.content || ''}`,
      )
      .join('\n\n')

    // ===== FASE 1 remota: sólo SSC → Agente 2 → IR =====
    // Bloque TRIAGE deterministico: inventario ya verificado localmente → el agente lo USA
    // en vez de re-derivarlo (menos tokens, mas precision, huecos declarados).
    const triageBlock = filesToSend
      .filter((f) => f.triage)
      .map((f) => triageToPromptBlock(f.triage))
      .join('\n\n')
    // Bloque FORMULARIOS_DETECTADOS (layout real de las WINDOW, sin IA) → A1 y luego A3
    const formsBlock = filesToSend
      .filter((f) => f.formsDraft && f.formsDraft.ok)
      .map((f) => formsDraftToPromptBlock(f.formsDraft))
      .join('\n\n')
    imp.formsBlock = formsBlock || ''
    const userPromptIR =
      `Procesá el siguiente archivo${imp.files.length > 1 ? 's' : ''} y devolvé EXCLUSIVAMENTE el IR JSON según el contrato del system prompt. NO agregues texto antes ni después salvo WARNINGS al final.\n\n` +
      (triageBlock ? triageBlock + '\n\n' : '') +
      (formsBlock ? formsBlock + '\n\n' : '') +
      `${filesBlock}\n\n` +
      (imp.instructions ? `Contexto adicional del usuario: ${imp.instructions}\n` : '')

    imp.busyInterpret = true
    imp.phase = 'interpret'
    imp.lastResult = null
    imp.logicJson = null
    imp.converterIr = null
    imp.conversionMode = ''
    imp.lastRawError = null
    emit()

    let ir = null
    try {
      const { ok, data, model, usage, raw } = await aiCallImporterAgent(agent, userPromptIR, true)
      if (!ok || !data.data) {
        imp.lastRawError = {
          message: data.error || 'El agente ' + agent + ' no devolvió IR JSON válido',
          hint: 'Revisá el raw abajo o editá el prompt del agente.',
          raw: data.raw || raw || '',
          rawExcerpt: (typeof (data.rawExcerpt || data.raw || raw) === 'string'
            ? data.rawExcerpt || data.raw || raw
            : JSON.stringify(data.rawExcerpt || data.raw || raw || '')
          ).slice(0, 1500),
        }
        imp.busyInterpret = false
        imp.phase = null
        if (usage) aiUsageRecord(model, usage, useSSC ? 'interpret_ssc' : 'interpret_sst')
        emit()
        return
      }
      ir = data.data
      try {
        getRequiredIRSummary(ir)
        debugAgent1IR('[agent1-debug] IR recibido', ir)
      } catch (summaryError) {
        const normalizedRaw = raw || JSON.stringify(ir || {}, null, 2)
        imp.lastRawError = {
          message: summaryError.message || 'Agente 1 no entregó la descripción funcional obligatoria en meta.summary.',
          hint: 'El Agente 1 debe devolver el resumen funcional en meta.summary o summary. Revisá el JSON crudo.',
          raw: normalizedRaw,
          rawExcerpt: String(normalizedRaw || '').slice(0, 1500),
        }
        imp.busyInterpret = false
        imp.phase = null
        if (usage) aiUsageRecord(model, usage, useSSC ? 'interpret_ssc' : 'interpret_sst')
        emit()
        return
      }
      imp.ir = ir
      // FLUJO CON CONFIRMACIÓN: la IA dice QUÉ HACE el script y FRENA. El usuario
      // confirma (o edita el texto / refina por chat) y recién ahí corre el Agente 3
      // ("✓ Es correcto — Generar"). Unificado con el camino por descripción.
      imp.interpretation = irToHumanText(ir)
      imp.interpretationApproved = false
      if (usage) aiUsageRecord(model, usage, useSSC ? 'interpret_ssc' : 'interpret_sst')
    } catch (err) {
      imp.busyInterpret = false
      imp.phase = null
      toast('Error en agente interprete (fase 1): ' + err.message, 'danger')
      emit()
      return
    }

    imp.busyInterpret = false
    imp.phase = null
    emit()
  }

  /* ===== Convertir desde el IR aprobado =====
     .sst fuente: convertidor determinista data.json + logic.json (sin Agente 3).
     .ssc/pseudo-fuente: conserva la ruta histórica por Agente 3. */
  async function aiImportConvert() {
    if (imp.busy) return
    if (!imp.interpretationApproved) {
      toast('Primero aprobá el IR del archivo antes de generar.', 'warning')
      return
    }

    // Construir input para el agente 3: el IR aprobado
    const ir =
      imp.ir ||
      (imp.interpretation
        ? (() => {
            try {
              return JSON.parse(imp.interpretation)
            } catch {
              return null
            }
          })()
        : null)
    if (!ir) {
      toast('No hay IR válido para convertir.', 'warning')
      return
    }

    const sourceSst = imp.files.find(aiFileIsSourceSST)
    if (sourceSst) {
      imp.busy = true
      imp.phase = 'convert_sst'
      imp.lastRawError = null
      emit()
      try {
        console.log('[sst-import] skip agent3 reason=deterministic-sst', sourceSst.name)
        const conversion = convertSstToBoston(normalizeReplacementChars(String(sourceSst.content || '')), {
          ...buildSstConversionOptions(sourceSst, imp.files),
        })
        const adapted = conversion.boston
        if (!adapted?.dataJson || !adapted?.logicJson?.entryBlock || !Object.keys(adapted.logicJson.blocks || {}).length) {
          throw new Error('El convertidor SST no produjo un paquete data.json + logic.json ejecutable.')
        }
        imp.lastResult = adapted.dataJson
        imp.logicJson = adapted.logicJson
        imp.converterIr = conversion.ir
        imp.conversionMode = 'sst_interpreter_v1'
        imp.handlerJs = null
        imp.handlerWarnings = []
        imp.lastWarnings = (adapted.diagnostics || [])
          .filter((item) => String(item.level || item.severity || '').toLowerCase() !== 'info')
          .map((item) => `[${item.code || 'SST'}] ${item.message || item.title || String(item)}`)
        imp.lastRawError = null
      } catch (err) {
        imp.lastResult = null
        imp.logicJson = null
        imp.converterIr = null
        imp.conversionMode = ''
        imp.lastRawError = { message: err.message, raw: '', rawExcerpt: '' }
      } finally {
        imp.busy = false
        imp.phase = null
        emit()
      }
      return
    }

    try {
      getRequiredIRSummary(ir)
      debugAgent1IR('[agent3-debug] IR enviado a Agente 3', ir)
    } catch (summaryError) {
      const serializedIR = JSON.stringify(ir || {}, null, 2)
      imp.lastRawError = {
        message: summaryError.message || 'Agente 1 no entregó la descripción funcional obligatoria en meta.summary.',
        hint: 'No se enviará a Agente 3 hasta que el IR tenga summary obligatorio.',
        raw: serializedIR,
        rawExcerpt: serializedIR.slice(0, 1500),
      }
      emit()
      return
    }

    const userPrompt =
      `Convertí el siguiente IR JSON a un Custom Script data.json siguiendo las reglas del Agente 3.\n\n` +
      (imp.interpretation && imp.interpretationApproved
        ? 'DESCRIPCIÓN CONFIRMADA POR EL USUARIO (contexto descriptivo; si difiere del IR estructurado, prevalece el IR y se agrega un warning):\n' + imp.interpretation + '\n\n'
        : '') +
      (imp.formsBlock ? imp.formsBlock + '\n\n' : '') +
      `${JSON.stringify(ir, null, 2)}`

    imp.busy = true
    emit()
    try {
      const { ok, data, model, usage, raw } = await aiGenerateScript(ir, userPrompt)
      if (!ok || !data.data) {
        imp.busy = false
        imp.lastRawError = {
          message: data.error || 'El Agente 3 no devolvió JSON válido',
          hint: data.hint || 'Revisá el raw para diagnosticar.',
          raw: data.raw || raw || '',
          rawExcerpt: (typeof (data.rawExcerpt || data.raw || raw) === 'string'
            ? data.rawExcerpt || data.raw || raw
            : JSON.stringify(data.rawExcerpt || data.raw || raw || '')
          ).slice(0, 1500),
          candidatesFound: data.candidatesFound || 0,
        }
        if (usage) aiUsageRecord(model, usage, 'convert')
        emit()
        return
      }
      imp.lastResult = preserveStrictSstCommandPlan(data.data, ir)
      imp.logicJson = null
      imp.handlerJs = null
      imp.handlerWarnings = []
      imp.converterIr = null
      imp.conversionMode = 'agent3'
      imp.lastWarnings = data.warnings || []
      if (!dataJsonEjecutable(imp.lastResult)) {
        imp.lastWarnings = [
          ...imp.lastWarnings,
          '⚠ El data.json llegó sin formularios NI comandos ejecutables (onLoad/postCommands/autoRun): respuesta parcial. Tocá "✓ Es correcto — Generar" otra vez; si el script es de ejecución directa, aclaralo en la descripción confirmada.',
        ]
      }
      imp.lastRawError = null
      if (usage) aiUsageRecord(model, usage, 'convert')
      imp.busy = false
      emit()
      // AGENTE 4 automatico tras regenerar el data.json desde el IR (solo si hay forms reales)
      if (dataJsonEjecutable(imp.lastResult)) await maybeAutoHandler('tras regenerar A3')
    } catch (err) {
      imp.busy = false
      imp.lastRawError = { message: err.message, raw: '', rawExcerpt: '' }
      emit()
    }
  }

  /* ===== CHAT DE REFINAMIENTO del IR (port de aiChatRefine) ===== */
  async function aiChatRefine() {
    if (imp.busyChat) return
    if (!imp.interpretation) {
      toast('Primero interpretá los archivos.', 'warning')
      return
    }
    if (isSstInterpreterResult(imp.lastResult)) {
      toast('El resultado SST ya fue construido por el convertidor. Los ajustes funcionales deben realizarse en Revisión Dev sobre data.json + logic.json.', 'warning')
      return
    }
    const msg = (chatInput || '').trim()
    if (!msg) return

    imp.chat = imp.chat || []
    imp.chat.push({ role: 'user', content: msg })
    setChatInput('')
    imp.busyChat = true
    emit()

    // Construir el contexto: interpretacion actual + historial del chat
    const filesBlock = imp.files.map((f) => `=== ${f.name} ===\n${f.content.slice(0, 8000)}`).join('\n\n')
    const conversationHistory = imp.chat.map((m) => `${m.role === 'user' ? '👤 USER' : '🤖 ASISTENTE'}: ${m.content}`).join('\n\n')
    const userPrompt =
      `Estamos refinando la interpretación de un script. Estos son los archivos originales:\n\n${filesBlock}\n\n` +
      `Esta es la interpretación actual (la podés actualizar si el usuario pide cambios):\n\n${imp.interpretation}\n\n` +
      `Y este es el historial de conversación con el usuario:\n\n${conversationHistory}\n\n` +
      `INSTRUCCIONES:\n` +
      `1. Respondé al último mensaje del usuario en castellano, BREVE y propositivo.\n` +
      `2. Si el usuario te pidió cambios sobre la interpretación (agregar campo, cambiar tipo, modificar comando, etc), aplicalos.\n` +
      `3. Al final de tu respuesta, SI hiciste cambios, agregá un bloque EXACTO con este formato:\n\n` +
      `INTERPRETACION_ACTUALIZADA:\n[acá la interpretación completa actualizada, en el mismo formato markdown que la actual]\n---\n\n` +
      `Si NO hiciste cambios, NO incluyas ese bloque.\n` +
      `NO generes JSON todavía — eso será un paso posterior cuando el usuario apruebe.`

    try {
      // El chat usa el MISMO agente que se usó para interpretar (agent1 o agent2)
      const agent = imp.usedAgent || 'agent1_sst'
      const { data, model, usage } = await aiCallImporterAgent(agent, userPrompt, false)
      const reply = (typeof data.data === 'string' ? data.data : '') || data.raw || 'No pude responder.'
      // Si la respuesta contiene un bloque IR_ACTUALIZADO o INTERPRETACION_ACTUALIZADA, aplicarlo
      const updMatchIr = reply.match(/IR_ACTUALIZADO:\s*([\s\S]+?)(?:\n---|$)/)
      const updMatchInt = reply.match(/INTERPRETACION_ACTUALIZADA:\s*([\s\S]+?)(?:\n---|$)/)
      let cleanReply = reply
      if (updMatchIr) {
        try {
          const newIr = JSON.parse(updMatchIr[1].trim())
          imp.ir = newIr
          imp.interpretation = JSON.stringify(newIr, null, 2)
        } catch (e) {
          /* IR no parseable, dejar como está */
        }
        cleanReply = reply.replace(/IR_ACTUALIZADO:[\s\S]+?(?:\n---|$)/, '').trim()
        cleanReply += '\n\n✏ IR actualizado arriba.'
      } else if (updMatchInt) {
        imp.interpretation = updMatchInt[1].trim()
        try {
          imp.ir = JSON.parse(imp.interpretation)
        } catch {}
        cleanReply = reply.replace(/INTERPRETACION_ACTUALIZADA:[\s\S]+?(?:\n---|$)/, '').trim()
        cleanReply += '\n\n✏ IR actualizado arriba.'
      }
      imp.chat.push({ role: 'assistant', content: cleanReply })
      if (usage) aiUsageRecord(model, usage, 'chat')
      imp.busyChat = false
      emit()
    } catch (err) {
      imp.chat.push({ role: 'assistant', content: 'Error: ' + err.message })
      imp.busyChat = false
      emit()
    }
  }

  /* ===== CHAT DE EDICION: aplica cambios al data.json YA generado (port de aiChatEditScript) ===== */
  async function aiChatEditScript() {
    if (imp.busyEdit) return
    if (!imp.lastResult) {
      toast('Primero generá un script.', 'warning')
      return
    }
    if (isSstInterpreterResult(imp.lastResult)) {
      toast('El Agente 3 está deshabilitado temporalmente para importaciones SST. Editá el paquete en Revisión Dev sin reemplazar su logic.json.', 'warning')
      return
    }
    const msg = (editInput || '').trim()
    if (!msg) return
    imp.editChat = imp.editChat || []
    imp.editChat.push({ role: 'user', content: msg })
    setEditInput('')
    imp.busyEdit = true
    emit()
    const userPrompt = 'MODO EDICION\nINSTRUCCION: ' + msg + '\nDATA_JSON_ACTUAL:\n' + JSON.stringify(imp.lastResult)
    try {
      const { ok, data, model, usage } = await aiCallImporterAgent('agent3_gen', userPrompt, true, {
        generationMode: 'edit',
        referenceIr: imp.ir || null,
      })
      if (usage) aiUsageRecord(model, usage, 'edit')
      if (ok && data.data) {
        const prevJson = JSON.stringify(imp.lastResult)
        const validationWarnings = []
        let candidate = preserveStrictSstCommandPlan(data.data, imp.ir)
        const normalized = normalizeDataJson(candidate)
        candidate = preserveStrictSstCommandPlan(normalized.data, imp.ir)
        validationWarnings.push(...normalized.changes.map((change) => '[NORMALIZE] ' + change))
        if (!dataJsonEjecutable(candidate)) {
          throw new Error('La edición devolvió un data.json sin formularios ni comandos ejecutables; no se aplicó.')
        }

        let lintReport = lintDataJson(candidate)
        const initialLintErrorCount = lintErrors(lintReport).length
        if (initialLintErrorCount) {
          const repairPrompt =
            lintFixPrompt(lintReport, candidate) +
            '\n\nCONTRATO DE CONSERVACIÓN: esta es una reparación técnica posterior a una edición solicitada por el usuario. No elimines campos, opciones, comandos, acciones ni alertas del DATA.JSON ACTUAL.\n\n' +
            'INSTRUCCIÓN ORIGINAL DE EDICIÓN:\n' + msg
          const repair = await aiCallImporterAgent('agent3_gen', repairPrompt, true, {
            generationMode: 'repair',
            referenceIr: imp.ir || null,
          })
          if (repair.usage) aiUsageRecord(repair.model, repair.usage, 'edit_repair')
          if (repair.ok && repair.data?.data) {
            let fixed = preserveStrictSstCommandPlan(repair.data.data, imp.ir)
            const fixedNormalized = normalizeDataJson(fixed)
            fixed = preserveStrictSstCommandPlan(fixedNormalized.data, imp.ir)
            const fixedLint = lintDataJson(fixed)
            const repairLosses = validateRepairPreservation(candidate, fixed)
            if (repairLosses.length) {
              console.warn('[import edit] reparación descartada por pérdida funcional:', repairLosses)
            }
            if (!repairLosses.length && dataJsonEjecutable(fixed) && lintErrors(fixedLint).length < initialLintErrorCount) {
              candidate = fixed
              lintReport = fixedLint
              validationWarnings.push(...fixedNormalized.changes.map((change) => '[REPAIR NORMALIZE] ' + change))
            }
          }
        }

        const finalLintErrors = lintErrors(lintReport)
        if (finalLintErrors.length) {
          throw new Error(
            'La edición conserva errores estructurales después del intento de reparación: ' +
              finalLintErrors.map((diagnostic) => diagnostic.message).join(' '),
          )
        }
        const mainTriage = (imp.files.find((file) => file.triage) || {}).triage
        const previousCoverageReport = mainTriage ? checkCoverage(mainTriage, imp.lastResult) : []
        const coverageReport = mainTriage ? checkCoverage(mainTriage, candidate) : []
        // Una familia nueva puede ser precisamente el cambio explícito pedido en el chat.
        // Sí bloqueamos que la edición introduzca una pérdida nueva respecto del resultado previo.
        const previousCoverageErrors = new Set(
          previousCoverageReport
            .filter((diagnostic) => diagnostic.severity === 'error')
            .map((diagnostic) => diagnostic.code + '|' + JSON.stringify(diagnostic.detail || '')),
        )
        const coverageErrors = coverageReport.filter(
          (diagnostic) =>
            diagnostic.severity === 'error' &&
            diagnostic.code !== 'COVERAGE_INVENTED_FAMILY' &&
            !previousCoverageErrors.has(diagnostic.code + '|' + JSON.stringify(diagnostic.detail || '')),
        )
        if (coverageErrors.length) {
          throw new Error(
            'La edición contradice el archivo importado y no se aplicó: ' +
              coverageErrors.map((diagnostic) => diagnostic.message).join(' '),
          )
        }

        imp.lastResult = candidate
        const agentWarnings = (Array.isArray(data.warnings) ? data.warnings : data.warnings ? [data.warnings] : [])
          .map((warning) => typeof warning === 'string' ? warning : String(warning?.message || warning?.title || ''))
          .filter(Boolean)
        const diagnosticWarnings = [...lintReport, ...coverageReport]
          .filter(
            (diagnostic) => diagnostic.severity === 'warning' || diagnostic.code === 'COVERAGE_INVENTED_FAMILY',
          )
          .map((diagnostic) => '[' + diagnostic.code + '] ' + diagnostic.message)
        imp.lastWarnings = [...agentWarnings, ...validationWarnings, ...diagnosticWarnings]
        const w = imp.lastWarnings.length ? ' ⚠ ' + imp.lastWarnings.join('; ') : ''
        imp.editChat.push({ role: 'assistant', content: 'Listo, apliqué: "' + msg + '".' + w })
        if (imp.logicJson) {
          imp.lastWarnings = [
            ...(imp.lastWarnings || []),
            'El data.json cambió por el chat: regenerá logic.json (Agente 4) para mantenerlo en sincronía.',
          ]
          imp.logicJson = null
          imp.handlerWarnings = []
        }
        // LECCIÓN (pendiente de OK humano): qué pidió el humano y QUÉ se aplicó (diff, no el script)
        try {
          const d = devDiffData(prevJson, JSON.stringify(candidate))
          lecQueue.add({
            origen: 'chat',
            script: String((candidate && candidate.title) || ''),
            instruccion: msg.slice(0, 300),
            resolucion: lecResumirDiff(d) || 'cambio aplicado (sin diff de campos)',
          })
        } catch (e) {
          console.warn('[leccion chat]', e)
        }
      } else {
        imp.editChat.push({ role: 'assistant', content: 'No pude aplicar el cambio: ' + (data.error || 'respuesta inválida del Agente 3') })
      }
    } catch (err) {
      imp.editChat.push({ role: 'assistant', content: 'Error: ' + err.message })
    }
    imp.busyEdit = false
    emit()
  }

  /* ===== AGENTE 4 — logic.json desde IR + data.json ===== */
  async function aiGenerateLogic() {
    if (imp.busyHandler) return
    if (!imp.lastResult) {
      toast('Primero generá el data.json (Agente 3).', 'warning')
      return
    }
    if (isSstInterpreterResult(imp.lastResult)) {
      toast('El importador SST ya generó su logic.json determinista; el Agente 4 no debe reemplazarlo.', 'warning')
      return
    }
    imp.busyHandler = true
    emit()
    try {
      const irTxt = imp.ir
        ? JSON.stringify(imp.ir, null, 2)
        : '(sin IR — el data.json vino de lenguaje natural; inferí la lógica del propio data.json)'
      const userPrompt = 'IR:\n' + irTxt + '\n\nDATA_JSON:\n' + JSON.stringify(imp.lastResult, null, 2)
      const { ok, data, model, usage } = await aiCallImporterAgent('agent4_scriptlogic', userPrompt, true, {
        generationMode: 'import',
        referenceIr: imp.ir || null,
      })
      if (!ok || !data?.data || typeof data.data !== 'object' || Array.isArray(data.data)) {
        throw new Error(data?.error || 'El Agente 4 no devolvió un logic.json válido.')
      }
      imp.logicJson = validateAgent4LogicMerge(imp.lastResult, data.data)
      imp.handlerJs = null
      imp.handlerWarnings = Array.isArray(data.data.warnings) ? data.data.warnings : []
      if (usage) aiUsageRecord(model, usage, 'logic_json')
    } catch (err) {
      toast('Error generando logic.json: ' + err.message, 'danger')
    }
    imp.busyHandler = false
    emit()
  }

  function aiDownloadLogic() {
    if (!imp.logicJson) {
      toast('No hay logic.json generado.', 'warning')
      return
    }
    const blob = new Blob([JSON.stringify(imp.logicJson, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'logic.json'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function copyLogic() {
    try {
      await navigator.clipboard.writeText(imp.logicJson ? JSON.stringify(imp.logicJson, null, 2) : '')
      setCopiedHandler(true)
      setTimeout(() => setCopiedHandler(false), 1200)
    } catch (e) {
      toast('No se pudo copiar.', 'danger')
    }
  }

  function copyRaw() {
    const rawRaw = (imp.lastRawError && (imp.lastRawError.raw || imp.lastRawError.rawExcerpt)) || ''
    const raw = typeof rawRaw === 'string' ? rawRaw : JSON.stringify(rawRaw)
    if (navigator.clipboard) navigator.clipboard.writeText(raw)
    toast('Raw copiado.', 'success')
  }

  /* ===== Vista previa funcional (port de showPreviewModal: single-file en iframe) ===== */
  async function openPreview(dataJson, configJson, opts = {}) {
    const title = (dataJson && dataJson.title) || state.cs.title || 'Custom Script'
    const realPreview = opts.realPreview === true
    if (realPreview && !state.sessionToken) {
      toast('Falta token Sabre para ejecutar comandos reales.', 'warning')
      return
    }
    pvRef.current = { dataJson, configJson, title, realPreview, logicJson: opts.logicJson || null, ir: opts.ir || null, prototype: opts.prototype === true }
    setPv({
      title,
      forms: (dataJson && dataJson.forms && dataJson.forms.length) || 0,
      post: (dataJson && dataJson.postCommands && dataJson.postCommands.length) || 0,
      uuid: (configJson && configJson.config && configJson.config.uuid) || '—',
      loading: true,
      error: null,
      url: null,
      realPreview,
      prototype: opts.prototype === true,
      prototypeRuntime: false,
      prototypeStats: opts.prototypeStats || null,
      logicJson: opts.logicJson || null,
      ir: opts.ir || null,
    })
    try {
      // Forzar refresh del cache del runtime para que el preview SIEMPRE use la última app.js de disco
      await csLoadRuntimeTemplates(true)
      const singleHtml = await csBuildSingleFile(dataJson, configJson, {
        previewMode: opts.prototype === true || !realPreview,
        simulateApi: opts.prototype === true || !realPreview,
        realPreview,
        token: opts.prototype === true ? '' : state.sessionToken,
        env: state.cs.targetEnv || 'stg',
        title,
        logicJson: opts.logicJson || null,
      })
      const blob = new Blob([singleHtml], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      setPv((p) => (p ? { ...p, url } : p))
    } catch (e) {
      console.error('[boston] error en preview:', e)
      setPv((p) => (p ? { ...p, loading: false, error: e.message || String(e) } : p))
    }
  }

  function closePreview() {
    if (pv && pv.url) setTimeout(() => URL.revokeObjectURL(pv.url), 100)
    setPv(null)
  }

  function togglePrototypeRuntime() {
    if (!mockModeEnabled) return
    setPv((current) => current && current.prototype
      ? { ...current, prototypeRuntime: !current.prototypeRuntime }
      : current)
  }

  // La X interna del script (runtime) avisa por postMessage cuando corre embebido.
  const closePreviewRef = useRef(null)
  closePreviewRef.current = closePreview
  useEffect(() => {
    function onScriptCloseMsg(e) {
      if (e && e.data && e.data.type === 'boston-script-close' && closePreviewRef.current) {
        closePreviewRef.current()
      }
    }
    window.addEventListener('message', onScriptCloseMsg)
    return () => window.removeEventListener('message', onScriptCloseMsg)
  }, [])

  function downloadPreviewHtml() {
    const ctx = pvRef.current
    if (!ctx) return
    if (ctx.prototype) {
      toast('Este prototipo sólo está habilitado para vista previa; todavía no es un script ejecutable.', 'warning')
      return
    }
    // Generamos un .html SIN previewMode para el archivo descargable (es el de producción)
    csBuildSingleFile(ctx.dataJson, ctx.configJson, { previewMode: false, title: ctx.title, logicJson: ctx.logicJson }).then((prodHtml) => {
      const pblob = new Blob([prodHtml], { type: 'text/html' })
      const purl = URL.createObjectURL(pblob)
      const a = document.createElement('a')
      a.href = purl
      const slug = (ctx.title + '').toLowerCase().replace(/[^a-z0-9_-]+/g, '_').slice(0, 40) || 'custom_script'
      a.download = `${slug}.html`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(purl), 1000)
    })
  }

  /* ===== AGENTE 4 AUTOMATICO: se encadena tras generar el data.json si el IR
     declara logica dinamica (lecturas de pantalla, ramas, calculos, fase on_load). ===== */
  function irNeedsHandler() {
    const ir = imp.ir
    if (!ir || typeof ir !== 'object') return false
    const cp = Array.isArray(ir.command_plan) ? ir.command_plan : []
    return Boolean(
      (Array.isArray(ir.screen_reads) && ir.screen_reads.length) ||
        (Array.isArray(ir.branches) && ir.branches.length) ||
        (Array.isArray(ir.computations) && ir.computations.length) ||
        (Array.isArray(ir.subflows) && ir.subflows.length) ||
        cp.some((c) => c && c.phase === 'on_load') ||
        ir.agent_initials_capture === true,
    )
  }
  async function maybeAutoHandler(motivo) {
    if (!imp.lastResult || imp.busyHandler) return
    if (!irNeedsHandler()) return
    try {
      await aiGenerateLogic()
    } catch (e) {
      imp.lastWarnings = [...(imp.lastWarnings || []), 'Agente 4 (' + motivo + ') fallo: ' + e.message]
      emit()
    }
  }

  /* ===== Envio a Revision Dev (port del action send-review; prompt() → modal) ===== */
  async function submitReview() {
    const result = reviewDraft?.dataJson || imp.lastResult
    if (!result || reviewSending) return
    const motivo = reviewMotivo
    setReviewSending(true)
    try {
      const _ir = reviewDraft?.ir || imp.converterIr || imp.ir || null
      const _logic = reviewDraft?.logicJson || imp.logicJson || null
      const _dj = JSON.stringify(result, null, 2)
      const _files = { 'data.json': _dj, 'feedback.txt': motivo || '' }
      if (_ir) _files['ir.json'] = JSON.stringify(_ir, null, 2)
      if (_logic) _files['logic.json'] = JSON.stringify(_logic, null, 2)
      const item = {
        id: 'rev_' + Date.now().toString(36),
        ts: new Date().toISOString(),
        title: result.title || '(sin titulo)',
        feedback: motivo || '(sin detalle)',
        estado: 'en_revision',
        data: result,
        ir: _ir,
        published: state.cs && state.cs.uuid ? { uuid: state.cs.uuid, name: state.cs.name || '' } : null,
        files: _files,
        original: _dj,
        history: [{ ts: new Date().toISOString(), action: 'enviado a revisión' }],
        origin: reviewDraft?.prototype ? 'admin_v2_sst_review' : 'admin_v2',
        sourceFiles: imp.files.map((file) => file.sourceFile).filter(Boolean),
        sourceFileIds: imp.files.map((file) => file.sourceFile?.id).filter(Boolean),
      }
      const saved = await devQueuePush(item)
      emit()
      setReviewOpen(false)
      setReviewDraft(null)
      if (saved.via === 'localStorage') {
        toast(
          'La revisión quedó visible localmente, pero la API no confirmó su registro en BD: ' + (saved.warn || 'error no identificado'),
          'warning',
          { title: 'Revisión pendiente de sincronizar' },
        )
        return
      }
      toast(
        '«' + (result.title || 'script') + '» quedó registrado con el ID ' + saved.id + ' y ya debe aparecer en Revisión Dev.',
        'success',
        { title: '🚨 Enviado a revisión de DEV.' },
      )
    } catch (e) {
      console.warn('dev queue:', e)
      toast(e.message || 'No se pudo enviar a Revisión Dev.', 'danger', { title: 'Error al enviar revisión' })
    } finally {
      setReviewSending(false)
    }
  }

  /* ===== Acciones del preview (port de bindAiPreview, prefix 'aii') ===== */
  async function doAction(action) {
    const result = imp.lastResult
    if (!result) return
    if (action === 'clear') {
      imp.lastResult = null
      imp.logicJson = null
      imp.converterIr = null
      imp.conversionMode = ''
      imp.lastWarnings = []
      state.ai.testLog = []
      emit()
      return
    }
    if (action === 'preview-modal') {
      if (!mockModeEnabled) return
      const cfg = { config: { uuid: 'preview-' + Date.now(), useInternalConfig: true } }
      openPreview(result, cfg, { logicJson: imp.logicJson, ir: imp.converterIr || imp.ir })
      return
    }
    if (action === 'preview-real') {
      const cfg = { config: { uuid: 'preview-' + Date.now(), useInternalConfig: true } }
      openPreview(result, cfg, { realPreview: true, logicJson: imp.logicJson, ir: imp.converterIr || imp.ir })
      return
    }
    if (action === 'send-review') {
      setReviewDraft(null)
      setReviewMotivo('')
      setReviewOpen(true)
      return
    }
    if (action === 'gen-handler') {
      await aiGenerateLogic()
      return
    }
    if (action === 'download-handler') {
      aiDownloadLogic()
      return
    }
    // apply / publish: ambos cargan el script al builder (igual que el original)
    const slug = (result.title || 'ia_script').toLowerCase().replace(/[^a-z0-9-_]+/g, '_')
    // FIX: uuid nuevo por resultado (memoizado) para no pisar el script anterior del builder
    result._uuid = result._uuid || uuidV4()
    // FICHA.md automatica (estrategia F): triage + IR + warnings → documentacion del script
    const sstTriage = (imp.files.find((f) => f.triage) || {}).triage || null
    let fichaMd = null
    try {
      fichaMd = buildFichaMd({ triage: sstTriage, ir: imp.ir, dataJson: result, name: slug, uuid: result._uuid, warnings: imp.lastWarnings })
    } catch (err) {
      console.warn('[ficha]', err)
    }
    const info = edApplyScript(
      result,
      { config: { uuid: result._uuid, useInternalConfig: true } },
      slug,
      null,
      fichaMd,
      imp.logicJson || null,
    )
    toast(`Script "${slug}" cargado en el builder — abrí la pestaña Custom Script. UUID: ${info.uuid} - Forms: ${info.forms} - Commands: ${info.commands}`, 'info', {
      title: 'Script cargado en el builder',
    })
    if (action === 'publish') {
      try {
        // OJO: el original destructura `res` aunque csPersistAndPublish devuelve `result`
        // (mostraba siempre Ruta: N/A) — se respeta la salida tal cual.
        const { item, res } = await csPersistAndPublish()
        try {
          await csProposeToKB({ name: item.name, title: item.title || result.title, dataJson: result })
        } catch (e) {}
        toast(
          `UUID: ${item.uuid} - Ruta: ${(res && res.path) || 'N/A'} - URL: http://localhost${(res && res.url) || ''}?token=ATH:...&env=${state.cs.targetEnv}`,
          'success',
          { title: 'Publicado.' },
        )
      } catch (e) {
        toast(`Error publicando: ${e.message}`, 'danger')
      }
    }
  }

  /* ===== Calculados de render (mismas expresiones del template original) ===== */
  const sscN = imp.files.filter((f) => aiFileIsSSC(f)).length
  const sstN = imp.files.filter((f) => !aiFileIsSSC(f)).length
  const meta = (imp.ir && imp.ir.meta) || {}
  let metaTxt = ''
  try {
    metaTxt = imp.ir ? getRequiredIRSummary(imp.ir) : ''
  } catch (_) {
    metaTxt = ''
  }
  const metaChip = (lbl, val) => {
    const v = Array.isArray(val) ? val.join(', ') : val || ''
    return v ? (
      <div style={{ fontSize: 12, marginTop: 3, color: 'var(--muted)' }}>
        <strong>{lbl}:</strong> {v}
      </div>
    ) : null
  }
  const r = imp.lastResult
  const resultForms = (r && r.forms) || []
  const totalFields = resultForms.reduce((s, f) => s + (f.remarks || []).length, 0)
  const genLabel = imp.busyInterpret
    ? imp.usedAgent === 'sst_converter_v1'
      ? '⏳ Fase 1/2 - Convertidor SST interpretando localmente, sin IA...'
      : '⏳ Fase 1/2 - Agente ' + (imp.usedAgent === 'agent2_ssc' ? '2' : '1') + ' interpretando archivo...'
    : imp.busy
      ? imp.phase === 'convert_sst'
        ? '⏳ Fase 2/2 - Convertidor SST construyendo data.json + logic.json...'
        : '⏳ Fase 2/2 - Agente 3 construyendo' +
          (imp.chunk ? ' — parte ' + imp.chunk.done + '/' + imp.chunk.total + ': ' + (imp.chunk.label || '') : ' Custom Script...')
      : '✨ Generar Custom Script desde archivo'
  const chunkPct = imp.chunk ? Math.round((imp.chunk.done / Math.max(1, imp.chunk.total)) * 100) : 0

  // Validaciones deterministas agregadas de todos los .sst cargados (regla: cualquier script)
  const triageDiagnostics = imp.files
    .filter((f) => f.triage && Array.isArray(f.triage.diagnostics))
    .flatMap((f) => f.triage.diagnostics)
  // Mejora 3: cobertura fuente → data.json (diff determinista post-generación)
  let coverageDiagnostics = []
  try {
    const mainTriage = (imp.files.find((f) => f.triage) || {}).triage
    if (mainTriage && imp.lastResult) coverageDiagnostics = checkCoverage(mainTriage, imp.lastResult)
  } catch (e) { console.warn('[coverage]', e) }
  // Lint determinista del data.json generado (placeholders↔tags, campos huérfanos, selects)
  let lintDiagnostics = []
  try {
    if (imp.lastResult && !isSstInterpreterResult(imp.lastResult)) {
      const normalized = normalizeDataJson(imp.lastResult)
      if (normalized.changes.length) {
        lintDiagnostics.push(...normalized.changes.map((c) => ({ code: 'NORMALIZE_FIX', severity: 'warning', message: 'Reparado automáticamente: ' + c, raw: '' })))
        imp.lastResult = normalized.data // usar SIEMPRE la versión reparada
      }
      lintDiagnostics.push(...lintDataJson(imp.lastResult).filter((d) => d.severity !== 'info'))
    }
  } catch (e) { console.warn('[lint]', e) }
  const allDiagnostics = [...triageDiagnostics, ...coverageDiagnostics, ...lintDiagnostics]
  const diagErrors = allDiagnostics.filter((d) => d.severity === 'error').length
  const diagWarnings = allDiagnostics.filter((d) => d.severity === 'warning').length

  return (
    <>
      <DiagnosticsModal open={diagOpen} onClose={() => setDiagOpen(false)} diagnostics={allDiagnostics} />
      <AgentTrace />
      <div className="page-head">
        <div>
          <h1>🤖 IA - Importador de archivos</h1>
          <p>
            Subí un <code>.sst</code> / <code>.ssc</code> y la IA lo convierte automáticamente en un Custom Script.{' '}
            Para <code>.sst</code>, el <strong>Agente 1</strong> interpreta y el convertidor determinista construye{' '}
            <code>data.json + logic.json</code> sin Agente 3. Los <code>.ssc</code> conservan la ruta de agentes.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hasKey ? <span className="bia-badge ok">🔑 API key OK</span> : <span className="bia-badge warn">⚠ Sin API key</span>}
          {state.sessionToken ? <span className="bia-badge ok">🔐 Token Sabre OK</span> : <span className="bia-badge warn">⚠ Sin token Sabre</span>}
          {allDiagnostics.length ? (
            <button
              type="button"
              className={`bia-badge ${diagErrors ? 'danger' : 'warn'}`}
              style={{ cursor: 'pointer', border: 0 }}
              onClick={() => setDiagOpen(true)}
              title="Ver validaciones deterministas del script"
            >
              {diagErrors ? '⛔' : '⚠️'} {allDiagnostics.length} validacion{allDiagnostics.length === 1 ? '' : 'es'}
              {diagErrors ? ` · ${diagErrors} error${diagErrors === 1 ? '' : 'es'}` : diagWarnings ? ` · ${diagWarnings} warn` : ''}
            </button>
          ) : null}
        </div>
      </div>

      {/* PASO 1: Cargar archivos */}
      <div className="card-x">
        <div className="bia-card-head">
          <h3>① Subir archivo</h3>
          <span className="bia-pill">{imp.files.length}</span>
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".zip,.sst,.ssc,.scribe,.csv,.xls,.xlsx,.txt,.ZIP,.SST,.SSC,.SCRIBE,.CSV,.XLS,.XLSX,.TXT"
          hidden
          onChange={onFiles}
        />
        <CButton color="secondary" variant="outline" onClick={() => fileRef.current && fileRef.current.click()}>
          <CIcon icon={icon('cilCloudUpload')} className="me-1" /> 📂 Agregar archivos
        </CButton>
        {imp.files.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
            {imp.files.map((f, idx) => (
              <React.Fragment key={idx}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: 'var(--bg)',
                  borderRadius: 6,
                  fontSize: 12.5,
                }}
              >
                <div>
                  <strong>{f.name}</strong>
                  <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
                    - {f.size} chars - {f.type}
                  </span>
                  {f.isBinary ? (
                    f.decompiled ? (
                      <span
                        className="bia-badge ok"
                        style={{ marginLeft: 8, fontSize: 10.5 }}
                        title="Decompilado estructural: tipo, longitud y obligatoriedad recuperados del binario"
                      >
                        🔬 binario - decompilado
                      </span>
                    ) : (
                      <span
                        className="bia-badge warn"
                        style={{ marginLeft: 8, fontSize: 10.5 }}
                        title="Archivo .ssc compilado - solo se extraen las cadenas legibles"
                      >
                        ⚙ binario - strings extraídos
                      </span>
                    )
                  ) : f.decompiled ? (
                    <span
                      className="bia-badge ok"
                      style={{ marginLeft: 8, fontSize: 10.5 }}
                      title="Bytecode decompilado a pseudo-fuente: defines, dropdowns, comandos en orden, ventanas e IF/GOTO recuperados"
                    >
                      🔬 .ssc decompilado → fuente (Agente 1)
                    </span>
                  ) : aiFileIsSourceSST(f) ? (
                    <span className="bia-pill mini" style={{ marginLeft: 8 }}>
                      → Agente 1 → Convertidor SST
                    </span>
                  ) : (
                    <span className="bia-pill mini" style={{ marginLeft: 8 }}>
                      → Agente 1
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {mockModeEnabled && String(f.type || '').toLowerCase() === 'sst' && !f.isBinary ? (
                    <CButton
                      color="success"
                      variant="outline"
                      size="sm"
                      disabled={!!sstPreviewBusy}
                      title="Convierte localmente a data.json + logic.json y abre una vista segura, sin agentes ni consumo de tokens"
                      onClick={() => previewDeterministicSst(f)}
                    >
                      {sstPreviewBusy === f.name ? 'Convirtiendo…' : 'Vista previa SST sin IA'}
                    </CButton>
                  ) : null}
                  <CButton color="danger" variant="outline" size="sm" title="Quitar archivo" onClick={() => removeFile(idx)}>
                    ×
                  </CButton>
                </div>
              </div>
              {f.triage ? <TriageCard t={f.triage} /> : null}
              {f.formsDraft && f.formsDraft.ok ? <FormsDraftCard d={f.formsDraft} /> : null}
              {sstPrototypeReport?.fileName === f.name ? (
                <div
                  style={{
                    margin: '4px 0 6px 12px',
                    padding: '7px 10px',
                    borderRadius: 7,
                    border: '1px solid rgba(34,217,137,.45)',
                    background: 'rgba(34,217,137,.07)',
                    fontSize: 12,
                  }}
                >
                  <strong>Conversión determinista:</strong> {sstPrototypeReport.outputForms} forms Boston · {sstPrototypeReport.menuForms} menú(s) ·{' '}
                  profundidad {sstPrototypeReport.maxMenuDepth} · {sstPrototypeReport.logicBlocks} bloques conservados
                  {sstPrototypeReport.unmappedForms ? ` · ${sstPrototypeReport.unmappedForms} forms sólo en logic.json` : ''}
                  {sstPrototypeReport.warnings ? ` · ${sstPrototypeReport.warnings} advertencia(s)` : ''}
                </div>
              ) : null}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p className="bia-empty" style={{ marginTop: 10 }}>
            Sin archivos cargados.
          </p>
        )}
        {sscN > 0 &&
          (sstN > 0 ? (
            <CAlert color="success" className="mb-0" style={{ marginTop: 10 }}>
              ✅ Detecté <b>.sst</b> + <b>.ssc</b>: uso el <b>.sst</b> (texto fuente = conversión fiel) e ignoro el <code>.ssc</code>.
            </CAlert>
          ) : imp.ir ? null : (
            <CAlert color="warning" className="mb-0" style={{ marginTop: 10 }}>
              ⚠ Subiste <b>solo .ssc</b> (binario compilado): la lectura directa es PARCIAL. Tocá <b>Interpretar</b> — la IA te va a
              decir <b>qué hace el script</b> para que lo confirmes (o lo completes) antes de generar. Si tenés el{' '}
              <code>.sst</code>, mejor: resultado fiel sin confirmación extra.
            </CAlert>
          ))}

        {imp.files.length ? (
          <>
            <div style={{ marginTop: 14 }}>
              <CFormLabel>Contexto extra (opcional)</CFormLabel>
              <CFormInput
                value={instructions}
                placeholder="Ej: el csv son los centros de costo, usalos en el select"
                onChange={(e) => {
                  setInstructions(e.target.value)
                  imp.instructions = e.target.value
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <CButton color="success" className="btn-brand" disabled={busy} style={{ fontSize: 14, padding: '10px 18px' }} onClick={aiInterpretFiles}>
                {genLabel}
              </CButton>
            </div>

            {/* Agente 1 interpreta; al confirmar, SST usa convertidor y omite Agente 3. */}
            {imp.ir && !imp.lastResult && !busy ? (
              <div className="card-x" style={{ marginTop: 14, border: '1px solid var(--brand)' }}>
                <div className="bia-card-head">
                  <h3>🧠 Esto hace el script — confirmá para generar</h3>
                  <span className="bia-pill mini">{imp.usedAgent === 'agent2_ssc' ? 'desde .ssc (parcial)' : 'desde .sst'}</span>
                </div>
                {imp.sscOnly ? (
                  <p className="bia-empty" style={{ color: '#b07b1a', margin: '4px 0 8px' }}>
                    ⚠ Interpretado desde el compilado: el flujo es parcial — revisá y completá la descripción antes de generar.
                  </p>
                ) : null}
                <CFormTextarea
                  rows={9}
                  className="bia-mono"
                  value={imp.interpretation || ''}
                  onChange={(event) => {
                    imp.interpretation = event.target.value
                    emit()
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <CButton
                    color="success"
                    className="btn-brand"
                    onClick={() => {
                      imp.interpretationApproved = true
                      aiImportConvert()
                    }}
                  >
                    ✓ Es correcto — Generar script
                  </CButton>
                  <CButton
                    color="primary"
                    variant="outline"
                    title="Manda esta interpretación confirmada al constructor por lenguaje natural (IA·Construir)"
                    onClick={() => {
                      const texto = String(imp.interpretation || '').trim()
                      if (!texto) {
                        toast('No hay interpretación para enviar.', 'warning')
                        return
                      }
                      imp.interpretationApproved = true
                      state.ai.userPrompt = texto
                      state.view = 'aiBuild'
                      emit()
                      toast('La interpretación confirmada quedó cargada en IA·Construir: revisala y generá desde ahí.', 'success', {
                        title: 'Enviada al constructor',
                      })
                    }}
                  >
                    → Usar en el constructor (lenguaje natural)
                  </CButton>
                  <CButton
                    color="secondary"
                    variant="ghost"
                    onClick={() => {
                      imp.ir = null
                      imp.interpretation = ''
                      emit()
                    }}
                  >
                    Descartar
                  </CButton>
                </div>
              </div>
            ) : null}
            {imp.chunk ? (
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 12,
                    color: 'var(--muted)',
                    marginBottom: 5,
                  }}
                >
                  <span>
                    ⏳ Generando por partes — {imp.chunk.done}/{imp.chunk.total}
                    {imp.chunk.label ? ' - ' + imp.chunk.label : ''}
                  </span>
                  <span>
                    <strong style={{ color: 'var(--brand-dark)' }}>{chunkPct}%</strong>
                  </span>
                </div>
                <div style={{ height: 10, background: 'var(--bg)', borderRadius: 5, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.max(4, chunkPct)}%`,
                      background: 'var(--brand, #22d989)',
                      transition: 'width .35s ease',
                    }}
                  />
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {/* PASO 2: CUSTOM SCRIPT GENERADO (visual) */}
      {r ? (
        <div className="card-x" style={{ border: '2px solid var(--brand, #22d989)' }}>
          <div className="bia-card-head" style={{ marginBottom: 8 }}>
            <h3>
              ② Custom Script generado <span className="bia-badge ok">{r.title || ''}</span>
            </h3>
          </div>

          {metaTxt ? (
            <div
              style={{
                background: 'rgba(99,102,241,.08)',
                borderLeft: '3px solid #6366f1',
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 12,
              }}
            >
              <strong>📋 Qué hace este script</strong> {meta.pattern ? <span className="bia-pill mini">{meta.pattern}</span> : null}
              <div style={{ marginTop: 5, fontSize: 13 }}>{metaTxt}</div>
              {metaChip('Entradas', meta.inputs)}
              {metaChip('Lee del PNR/entorno', meta.reads)}
              {metaChip('Escribe', meta.writes)}
              {metaChip('No hace', meta.does_not)}
            </div>
          ) : null}

          {imp.lastWarnings && imp.lastWarnings.length ? (
            <CAlert color="warning">
              <strong>⚠ WARNINGS:</strong>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {imp.lastWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </CAlert>
          ) : null}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 6,
              margin: '8px 0 14px',
              fontSize: 13,
              padding: 10,
              background: 'var(--bg)',
              borderRadius: 6,
            }}
          >
            <div>
              <strong>Título:</strong> <code>{r.title || ''}</code>
            </div>
            <div>
              <strong>Pasos (wizard):</strong> {resultForms.length}
            </div>
            <div>
              <strong>Campos totales:</strong> {totalFields}
            </div>
            <div>
              <strong>PNR activo:</strong> {r.validateActiveReservation ? '✓ requerido' : '○ no requerido'}
            </div>
            <div>
              <strong>Ejecuta comandos:</strong> {r.sendCommands ? '✓ sí' : '○ no'}
            </div>
            <div>
              <strong>Ambiente:</strong> {r.targetEnv || 'stg'}
            </div>
          </div>

          {/* Botones de acción - prominentes arriba */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 18,
              padding: 12,
              background: 'linear-gradient(135deg,rgba(34,217,137,.08),rgba(34,217,137,.02))',
              borderRadius: 8,
              border: '1px dashed var(--brand-dark, #16a34a)',
            }}
          >
            {mockModeEnabled ? (
              <CButton color="success" className="btn-brand" title="Abre el form con llamadas simuladas" onClick={() => doAction('preview-modal')}>
                Vista previa segura
              </CButton>
            ) : null}
            <CButton color="success" variant="outline" title="Usa el token Sabre y puede ejecutar comandos reales" onClick={() => doAction('preview-real')}>
              Vista previa real con token
            </CButton>
            <CButton color="success" className="btn-brand" onClick={() => doAction('publish')}>
              💾 Guardar y publicar
            </CButton>
            <CButton color="secondary" variant="outline" onClick={() => doAction('apply')}>
              ✏ Cargar en el Builder (editar antes)
            </CButton>
            <CButton
              color="secondary"
              variant="outline"
              disabled={!!imp.busyHandler}
              style={{ borderColor: '#6366f1', color: '#4338ca' }}
              title="Agente 4: genera logic.json con la lógica dinámica ejecutable (lecturas, ramas y on_load)"
              onClick={() => doAction('gen-handler')}
            >
              {imp.busyHandler ? '⏳ Generando logic.json...' : imp.logicJson ? '⚙ Regenerar logic.json (Agente 4)' : '⚙ Generar logic.json (Agente 4)'}
            </CButton>
            <CButton
              color="warning"
              variant="ghost"
              style={{ borderColor: '#f59e0b', color: '#b45309' }}
              title="No cumple las condiciones: mandalo a la cola del dev con tu feedback"
              onClick={() => doAction('send-review')}
            >
              🚨 Enviar a revisión (dev)
            </CButton>
            <CButton color="danger" variant="ghost" onClick={() => doAction('clear')}>
              🗑 Descartar
            </CButton>
          </div>

          <ScriptFieldsPreview r={r} />

          {/* Lógica declarativa del script (Agente 4 o convertidor SST) */}
          {imp.logicJson ? (
            <>
              <h4 style={{ margin: '16px 0 8px', color: '#4338ca', fontSize: 14 }}>
                ⚙ Lógica del script — <code>logic.json</code>
              </h4>
              {imp.handlerWarnings && imp.handlerWarnings.length ? (
                <CAlert color="warning">
                  <strong>⚠ WARNINGS de logic.json:</strong>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {imp.handlerWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </CAlert>
              ) : null}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
                <CButton size="sm" color="success" className="btn-brand" onClick={() => doAction('download-handler')}>
                  💾 Descargar logic.json
                </CButton>
                <CButton size="sm" color="secondary" variant="ghost" onClick={copyLogic}>
                  {copiedHandler ? '✓ Copiado' : '📋 Copiar'}
                </CButton>
                <span className="bia-pill mini">acompaña al data.json en el paquete del script</span>
              </div>
              <pre
                className="bia-pre"
                style={{ maxHeight: 360, overflow: 'auto', fontSize: 11, border: '1px solid #c7d2fe', borderRadius: 6 }}
              >
                {JSON.stringify(imp.logicJson, null, 2)}
              </pre>
            </>
          ) : (
            <div
              style={{
                margin: '16px 0 0',
                padding: '10px 12px',
                background: 'rgba(99,102,241,.06)',
                border: '1px dashed #6366f1',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--muted)',
              }}
            >
              ⚙ <strong>Lógica (Agente 4):</strong> si el <code>data.json</code> necesita lógica dinámica adicional
              (lecturas de pantalla, ramas, cálculos u on_load), generá <code>logic.json</code> con el botón de arriba.
            </div>
          )}

          {/* IR original colapsable + JSON crudo */}
          <details style={{ marginTop: 14 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: 12 }}>
              📝 Ver IR del Agente {imp.usedAgent === 'agent2_ssc' ? '2' : '1'} (interpretación intermedia)
            </summary>
            <pre className="bia-pre" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto', fontSize: 11 }}>
              {imp.interpretation || ''}
            </pre>
          </details>
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: 12 }}>
              💾 Ver data.json del Custom Script (lo que se publica)
            </summary>
            <pre className="bia-pre" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto', fontSize: 11 }}>
              {JSON.stringify(r, null, 2)}
            </pre>
          </details>

          {/* Chat de EDICIÓN: queda ABIERTO y edita el data.json generado (modo edición) */}
          <div
            style={{
              marginTop: 18,
              padding: 14,
              background: 'var(--bg)',
              borderRadius: 8,
              border: '1px solid var(--brand, #22d989)',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-dark, #16a34a)', marginBottom: 4 }}>
              💬 Refiná el script (chat)
            </div>
            <small style={{ color: 'var(--muted)', display: 'block', marginBottom: 10 }}>
              Pedí cambios en lenguaje natural y se aplican al toque sobre el script de arriba. Ej: "sacá el campo CUIT", "hacé el
              email obligatorio", "agregá un select de moneda ARS/USD", "ocultá la línea 3".
            </small>
            <ChatLog messages={imp.editChat} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <CFormTextarea
                rows={2}
                value={editInput}
                placeholder="Ej: 'sacá el campo CUIT', 'hacé el email obligatorio'..."
                style={{ flex: 1, fontSize: 12.5 }}
                onChange={(e) => setEditInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) aiChatEditScript()
                }}
              />
              <CButton color="success" className="btn-brand" size="sm" disabled={!!imp.busyEdit} onClick={aiChatEditScript}>
                {imp.busyEdit ? '⏳ Aplicando...' : '↵ Aplicar'}
              </CButton>
            </div>
            {(imp.editChat || []).length ? (
              <div style={{ marginTop: 8 }}>
                <CButton
                  size="sm"
                  color="secondary"
                  variant="ghost"
                  onClick={() => {
                    imp.editChat = []
                    emit()
                  }}
                >
                  🗑 Limpiar chat
                </CButton>
              </div>
            ) : null}

            {/* Avanzado: refinar el IR y re-generar (flujo anterior) */}
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--muted)' }}>
                ⚙ Avanzado: refinar la interpretación (IR) y re-generar
              </summary>
              <div style={{ marginTop: 10 }}>
                <ChatLog messages={imp.chat} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <CFormTextarea
                    rows={2}
                    value={chatInput}
                    placeholder="Ajustar la interpretación (IR)..."
                    style={{ flex: 1, fontSize: 12.5 }}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) aiChatRefine()
                    }}
                  />
                  <CButton size="sm" color="secondary" variant="outline" disabled={!!imp.busyChat} onClick={aiChatRefine}>
                    {imp.busyChat ? '⏳' : '↵ Enviar'}
                  </CButton>
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <CButton size="sm" color="secondary" variant="outline" disabled={!!imp.busy} onClick={aiImportConvert}>
                    {imp.busy ? '⏳ Re-generando...' : '🔄 Re-generar desde IR refinado'}
                  </CButton>
                  {(imp.chat || []).length ? (
                    <CButton
                      size="sm"
                      color="secondary"
                      variant="ghost"
                      onClick={() => {
                        imp.chat = []
                        emit()
                      }}
                    >
                      🗑 Limpiar chat
                    </CButton>
                  ) : null}
                </div>
              </div>
            </details>
          </div>
        </div>
      ) : null}

      {/* ERROR de conversion: mostrar raw response de Claude para debug */}
      {imp.lastRawError ? (
        <div className="card-x" style={{ border: '1px solid #dc2626' }}>
          <div className="bia-card-head">
            <h3 style={{ color: '#dc2626' }}>⚠ Error al convertir a JSON</h3>
            <CButton
              size="sm"
              color="secondary"
              variant="ghost"
              onClick={() => {
                imp.lastRawError = null
                emit()
              }}
            >
              × Cerrar
            </CButton>
          </div>
          <CAlert color="danger">
            <strong>{imp.lastRawError.message}</strong>
            {imp.lastRawError.hint ? (
              <>
                <br />
                <small>{imp.lastRawError.hint}</small>
              </>
            ) : null}
          </CAlert>
          {imp.lastRawError.candidatesFound !== undefined ? (
            <small style={{ color: 'var(--muted)' }}>Candidatos JSON encontrados: {imp.lastRawError.candidatesFound}</small>
          ) : null}
          {imp.lastRawError.rawExcerpt ? (
            <>
              <h4 style={{ margin: '14px 0 6px' }}>Respuesta cruda de Claude (primeros 1500 chars):</h4>
              <pre className="bia-pre" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 11.5 }}>
                {imp.lastRawError.rawExcerpt}
              </pre>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <CButton
                  size="sm"
                  color="secondary"
                  variant="outline"
                  onClick={() => {
                    imp.lastRawError = null
                    aiImportConvert()
                  }}
                >
                  🔄 Reintentar conversión
                </CButton>
                <CButton size="sm" color="secondary" variant="ghost" onClick={copyRaw}>
                  📋 Copiar raw
                </CButton>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {/* ===== Modal: Vista previa funcional (port de showPreviewModal) ===== */}
      <CModal visible={!!pv} onClose={closePreview} size="xl" alignment="center" scrollable={false}>
        {pv ? (
          <CModalBody style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'min(90vh, 860px)' }}>
            <div
              style={{
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'linear-gradient(135deg,#0f766e,#0e7490)',
                color: '#fff',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  {pv.prototype
                    ? (pv.prototypeRuntime ? 'Ejecución SST simulada - Sin IA' : 'Vista previa SST determinista - Sin IA')
                    : 'Vista previa funcional - Single-file'}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{pv.title}</div>
              </div>
              {!pv.prototype ? (
                <CButton
                  style={{ background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }}
                  title="Descargar .html autocontenido"
                  onClick={downloadPreviewHtml}
                >
                  ⬇ Descargar HTML único
                </CButton>
              ) : null}
              {pv.prototype && mockModeEnabled ? (
                <CButton
                  style={{ background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }}
                  onClick={togglePrototypeRuntime}
                >
                  {pv.prototypeRuntime ? 'Ver estructura' : 'Probar ejecución simulada'}
                </CButton>
              ) : null}
              {pv.prototype && mockModeEnabled ? (
                <CButton
                  color="warning"
                  onClick={() => {
                    setReviewDraft(pvRef.current)
                    setReviewMotivo('')
                    setReviewOpen(true)
                  }}
                >
                  Enviar a DEV
                </CButton>
              ) : null}
              {!pv.prototype ? (
                <CButton
                  style={{ background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }}
                  title="Abrir en pestaña nueva"
                  disabled={!pv.url}
                  onClick={() => window.open(pv.url, '_blank')}
                >
                  ↗ Pestaña nueva
                </CButton>
              ) : null}
              <CButton style={{ background: '#ef4444', color: '#fff', border: 0 }} title="Cerrar" onClick={closePreview}>
                × Cerrar
              </CButton>
            </div>
            <div
              style={{
                background: '#fef3c7',
                color: '#92400e',
                padding: '8px 20px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderBottom: '1px solid #fcd34d',
              }}
            >
              <span>⚠</span>
              <span>
                <b>{pv.prototype ? 'Prototipo de conversión:' : pv.realPreview ? 'Vista previa real:' : 'Vista previa segura:'}</b>{' '}
                {pv.prototype
                  ? (pv.prototypeRuntime
                    ? 'ejecuta el runtime final, pero api.js intercepta todas las llamadas Sabre.'
                    : 'usa el intérprete del Convertidor SST: respeta ventanas, botones, menús, CALL, GOTO e IF. Los comandos se registran, pero no se envían a Sabre.')
                  : pv.realPreview
                  ? 'usa el token Sabre y puede ejecutar comandos reales.'
                  : 'las llamadas a Sabre estan simuladas. El form, validacion, postCommands y placeholders se ejecutan reales.'}
              </span>
            </div>
            <div style={{ flex: 1, position: 'relative', background: '#f8fafc' }}>
              {pv.prototype && !pv.prototypeRuntime ? (
                <div style={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
                  <SstInterpreterPreview ir={pv.ir || {}} logicJson={pv.logicJson || {}} fileName={pv.title} />
                </div>
              ) : null}
              {(!pv.prototype || pv.prototypeRuntime) && pv.loading && !pv.error ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    font: '500 14px/1.5 system-ui',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                    <div>Empaquetando script en un único archivo…</div>
                  </div>
                </div>
              ) : null}
              {(!pv.prototype || pv.prototypeRuntime) && pv.error ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#dc2626',
                    font: '500 14px/1.5 system-ui',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>⚠</div>
                    <div>
                      <b>Error armando el preview</b>
                    </div>
                    <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>{pv.error}</div>
                    <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6 }}>Revisá la consola para detalles.</div>
                  </div>
                </div>
              ) : null}
              {(!pv.prototype || pv.prototypeRuntime) && pv.url ? (
                <iframe
                  title="boston-preview"
                  src={pv.url}
                  sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                  style={{ width: '100%', height: '100%', border: 0, background: '#fff', display: pv.loading ? 'none' : 'block' }}
                  onLoad={() => setPv((p) => (p ? { ...p, loading: false } : p))}
                />
              ) : null}
            </div>
            <div
              style={{
                padding: '8px 20px',
                background: '#f1f5f9',
                borderTop: '1px solid #e2e8f0',
                font: '12px/1.4 system-ui',
                color: '#475569',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                <b>{pv.prototype ? pv.prototypeStats?.sourceForms || 0 : pv.forms}</b> {pv.prototype ? 'ventana(s) SST' : 'form(s)'} - <b>{pv.post}</b> postCommand(s)
                {pv.prototypeStats ? ` · profundidad de menú ${pv.prototypeStats.maxMenuDepth} · ${pv.prototypeStats.logicBlocks} bloques logic.json` : ''}
              </span>
              <span style={{ opacity: 0.7 }}>
                UUID: <code>{pv.uuid}</code>
              </span>
            </div>
          </CModalBody>
        ) : null}
      </CModal>

      {/* ===== Modal: Enviar a revisión de DEV (reemplaza el prompt() nativo) ===== */}
      <CModal visible={reviewOpen} onClose={() => { if (!reviewSending) { setReviewOpen(false); setReviewDraft(null) } }} alignment="center">
        <CModalHeader>
          <CModalTitle>🚨 Enviar a revisión de DEV</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormLabel>¿Qué está mal o qué falta? (feedback para el dev):</CFormLabel>
          <CFormTextarea rows={3} value={reviewMotivo} onChange={(e) => setReviewMotivo(e.target.value)} />
        </CModalBody>
        <CModalFooter>
          <CButton color="light" disabled={reviewSending} onClick={() => { setReviewOpen(false); setReviewDraft(null) }}>
            Cancelar
          </CButton>
          <CButton color="warning" disabled={reviewSending} onClick={submitReview}>
            {reviewSending ? 'Enviando…' : 'Enviar a revisión'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AiImportView
